/**
 * The English source text. **Not a translation** — it is what every other
 * language is translated *from*, and what every untranslated key falls back to.
 *
 * A TypeScript module rather than a JSON file, and for a reason beyond taste:
 * a JSON import needs a runtime `with { type: 'json' }` attribute that the API's
 * module setting rejects, so the same file cannot be loaded by a Node service
 * and a Vite bundle without one of them being configured around it. It is also
 * more honest — this catalog is code, which is the entire premise of the split.
 *
 * Adding a key here is what makes it translatable. Nothing else can.
 */
export const EN_SOURCE = {
  "common": {
    "cancel": "Cancel",
    "remove": "Remove",
    "signOut": "Sign out",
    "language": "Language",
    "none": "—",
    "notRecorded": "Not recorded",
    "loading": "Loading",
    "working": "Working…"
  },
  "page": {
    "R-02": "Catalog",
    "R-03": "Product",
    "R-04": "Bulk order entry",
    "R-05": "Cart",
    "R-06": "Orders",
    "R-07": "Order",
    "R-23": "Consignment Invoices",
    "R-16": "Statement",
    "R-18": "Account",
    "A-14": "Order queue",
    "A-15": "Order",
    "shop": "Shop",
    "groupInventory": "My stock",
    "groupFinance": "Finance"
  },
  "auth": {
    "eyebrow": "Wholesale",
    "signIn": "Sign in",
    "email": "Email address",
    "emailPlaceholder": "you@company.com",
    "emailRequired": "Enter your email address.",
    "emailInvalid": "Enter a valid email address, for example you@company.com.",
    "passwordRequired": "Enter your password.",
    "password": "Password",
    "forgot": "Forgot password?",
    "showPassword": "Show password",
    "hidePassword": "Hide password",
    "submit": "Sign in",
    "submitting": "Signing in…",
    "newHere": "New to Miraki Wholesale?",
    "applyCta": "Apply for an account",
    "backToSignIn": "Back to sign in",
    "signInFailed": "We couldn't sign you in. Please try again.",
    "reset": {
      "title": "Reset your password",
      "body": "Enter the email address you sign in with. We'll send a code you can use to choose a new password.",
      "send": "Send reset code",
      "codeTitle": "Check your email",
      "codeBody": "If {{email}} belongs to a portal account, we've sent it a reset code. It's valid for 15 minutes. Enter it below with your new password.",
      "code": "Reset code",
      "submit": "Set new password",
      "noCode": "No email after a few minutes? Check spam, or",
      "askAgain": "send another code",
      "doneTitle": "Your password has been changed",
      "doneBody": "Sign in with your new password. For your security, every device you had trusted will ask for a sign-in code again.",
      "signInNow": "Sign in",
      "failed": "Something went wrong. Please try again."
    },
    "device": {
      "title": "Check your email",
      "body": "This browser hasn't been used with your account before. We've sent a 6-digit code to {{sentTo}}.",
      "code": "Sign-in code",
      "submit": "Verify and sign in",
      "trustNote": "Once verified, this browser is trusted for 30 days.",
      "resend": "Send a new code",
      "resent": "A new code is on its way. The previous one no longer works.",
      "resendLimited": "You've asked for several codes in a short time. Use the last one we sent, or wait 15 minutes.",
      "failed": "Could not verify the code. Please try again."
    },
    "changePassword": {
      "title": "Choose your password",
      "body": "Your account was just approved and a one-time password was emailed to you. Choose your own before continuing.",
      "current": "Temporary password",
      "new": "New password",
      "confirm": "Confirm new password",
      "submit": "Set password and continue",
      "tooShort": "Your new password needs to be at least 12 characters.",
      "mismatch": "The two passwords you typed don't match.",
      "failed": "Could not change your password"
    },
    /*
     * ON-12 — DO NOT SHIP TO A REAL RETAILER AS IT STANDS.
     *
     * These strings describe the pre-v1.6 arrangement: an advance percentage, a
     * credit limit, a delivery term, and prices Sales could revise during order
     * review. All four were retired when the business moved to consignment, and
     * the portal RECORDS this acceptance against a user, a timestamp and a
     * version — so a retailer accepting today is agreeing to a description of a
     * commercial model that does not exist.
     *
     * Replacement wording is drafted and awaiting Miraki's approval in
     * docs/Miraki_B2B_Consignment_Terms_v0.1.md. It is deliberately NOT applied
     * here: two points it turns on — when title passes, and who bears loss and
     * insures a piece while a retailer holds it (D39) — are Miraki's to state,
     * not ours to invent. Replacing one inaccurate description with another
     * would be worse, because this text is evidence.
     */
    "acceptTerms": {
      "title": "Accept trading terms",
      "body": "Before you can browse the catalog or place an order, please confirm you accept Miraki Wholesale's trading terms and conditions.",
      "summaryTitle": "What you're agreeing to",
      "summaryConsignment": "Miraki places pieces with you on consignment. They remain Miraki's until you report a sale, and only a piece you have sold becomes payable.",
      "summaryBudget": "You hold stock up to the inventory budget agreed for your account. Reporting a sale frees that piece's share of it.",
      "summaryPaymentTerms": "Any payment terms set for your account: invoices for pieces you have sold fall due on those terms.",
      "paymentTermsNotSet": "Payment terms have not been set for your account yet. You will be asked to review and accept them when they are.",
      "summaryPricing": "Wholesale prices shown are yours at the moment you submit an order; Sales may revise them during review and you accept or decline the revision.",
      "summaryLedger": "Every charge and payment against your account is recorded on your statement and cannot be edited after the fact — only reversed with a new entry.",
      "checkbox": "I have read and accept these trading terms and conditions.",
      "submit": "Accept and continue",
      "failed": "Could not record your acceptance",
      "revisedTitle": "Your payment terms have changed",
      "revisedBody": "Miraki has revised the payment terms for your account. Please review and accept them to continue. Orders already approved keep the terms they were approved under.",
      "paymentTermsTitle": "Your payment terms",
      "paymentTermsReason": "Note from Miraki: {{reason}}",
      "paymentTermsCheckbox": "I have reviewed and accept these payment terms."
    }
  },
  "analytics": {
    "title": "Analytics",
    "sub": "{{from}} to {{to}} · figures as of {{today}}",
    "from": "From", "to": "To", "clear": "Clear filters",
    "filter": { "retailerId": "Retailer", "groupId": "Group", "categoryId": "Category" },
    "subject": {
      "inventory": "Inventory & ageing", "sales": "Sales & performance", "lead-time": "Lead time",
      "returns": "Returns", "budget": "Budget utilisation", "payables": "Payables", "subscriptions": "Subscriptions",
      /* AN-13. The subject KEY is "non-moving" because it cites the requirement in code; the
         label is not, and the phrase must not reach a reader — see `holdings` below. */
      "non-moving": "Quarterly holdings"
    },
    "export": "Export CSV", "exportFailed": "Could not export the report. Please try again.",
    "compare": "Compare portals", "backToPortal": "Back to this portal",
    "compareTitle": "Portals side by side", "combined": "Combined figures", "portal": "Portal",
    "noTotal": "No combined total.", "notYet": "Not yet available.",
    "emptyTitle": "Nothing to show", "emptyBody": "There is no data for this view yet, or nothing matches the filters.",
    "colName": "Name", "pieces": "Pieces", "wholesale": "Wholesale", "orders": "Orders", "invoices": "Invoices",
    "piecesHeld": "Pieces held", "wholesaleHeld": "Wholesale held", "rpHeld": "RP held", "asOf": "as of {{day}}",
    "ageing": "Ageing", "days": "{{band}} days",
    "fast": "Fast sellers", "slow": "Slow-moving (held over {{days}} days, no sale)", "sold": "Sold",
    "heldOver": "Held over threshold", "oldest": "Oldest (days)",
    "byCategory": "By category", "byRetailer": "By retailer", "byProduct": "By product", "byGroup": "By group",
    "byCollection": "By collection", "byPricePoint": "By price point",
    "piecesSold": "Pieces sold", "wholesaleSold": "Wholesale sold", "avgDaysToSale": "Average days to sale",
    "avgDays": "Avg days", "daysByGroup": "Days to sale by group", "daysByColour": "Days to sale by gold colour",
    "performance": "Retailer performance", "sellThrough": "Sell-through", "onTime": "Paid on time",
    "daysLate": "Days late", "completed": "{{n}} completed", "distribution": "Order-to-receipt distribution",
    "stage": { "toConfirm": "Days to confirm", "toShip": "Days to ship", "toReceive": "Days to receive", "total": "Days, order to receipt" },
    "pastPromised": "Past their promised dispatch date", "retailer": "Retailer", "dispatchBy": "Dispatch by",
    "returnRate": "Return rate", "returnedOf": "{{a}} returned of {{b}} received", "returned": "Returned",
    "received": "Received", "rate": "Rate", "wholesaleReturned": "Wholesale returned",
    "daysUntilReturned": "Avg days held until returned",
    /* AN-11's budget breakdown, read at each retailer's latest daily sample (BG-08). */
    "sampledOn": "Sampled on", "headroom": "Headroom", "utilisation": "In use", "lowFor": "Days low",
    "outstanding": "Outstanding", "overdue": "Overdue", "notInvoiced": "Sold, not yet invoiced", "open": "Open",
    "renewalsDue": "Renewals due", "renewalRate": "Renewal rate", "renewedOf": "{{a}} of {{b}} renewed",
    "lapsed": "Lapsed", "lapses": "Lapses", "renewsOn": "Renewal date", "feesRecognised": "Fees recognised",
    /*
     * AN-13 quarterly holdings — the eighth A-42 tab.
     *
     * THE TONE IS THE REQUIREMENT. This is what the client asked for INSTEAD of a holding limit
     * (D20), so it opens a conversation and issues no verdict: nothing becomes payable, nothing is
     * recalled, and no sentence here may imply either. The words *non-moving*, *overdue*,
     * *threshold*, *alarm*, *stale* and *idle* appear in no string in this block, and
     * `screens/analytics-non-moving.spec.ts` asserts it over every key under `analytics.holdings`.
     *
     * The counted sentences are plural SETS rather than "{{n}} piece(s)": English has a plural
     * rule and the catalog states it (see `i18n/copy.spec.ts`).
     */
    "holdings": {
      "quarter": "Quarter",
      "snapped": "Showing {{quarter}}, the quarter containing the dates you chose.",
      "running": "{{quarter}} has not finished. These figures will change until {{to}}.",
      "cohortHeading": "What moved in {{quarter}}",
      "cohortSentence": "{{name}} held {{opening}} pieces on {{from}}. {{sold}} sold, {{returned}} came back, {{lost}} were reported lost, {{stillHeld}} were still there on {{to}}.",
      "stillHeld": "Still there on {{to}}",
      "heldToday": "Held today",
      "twoNumbers": "Still there on {{to}} is the quarter's closing count. Held today is as at {{asOf}} and includes pieces received since.",
      "rankingHeading": "Held longest",
      "jobNumber": "Job Number",
      "receivedOn": "Received",
      "daysHeld": "Days held",
      "showPieces": "Show pieces",
      "hidePieces": "Hide pieces",
      "firstReceipt": "First receipt {{day}}",
      "excluded": {
        "ON_APPROVED_RETURN_one": "Not listed: {{count}} piece on an approved return — {{miraki}} asked for back by Miraki, {{retailer}} requested by the retailer.",
        "ON_APPROVED_RETURN_other": "Not listed: {{count}} pieces on an approved return — {{miraki}} asked for back by Miraki, {{retailer}} requested by the retailer.",
        "NO_RECEIPT_DATE_one": "Not listed: {{count}} piece whose received date was not recorded.",
        "NO_RECEIPT_DATE_other": "Not listed: {{count}} pieces whose received date was not recorded."
      },
      "notShown": {
        "TRADING_UNDER_ONE_QUARTER_one": "Not shown: {{count}} account trading for less than one quarter.",
        "TRADING_UNDER_ONE_QUARTER_other": "Not shown: {{count}} accounts trading for less than one quarter.",
        "WINDING_DOWN_one": "Not shown: {{count}} account winding down.",
        "WINDING_DOWN_other": "Not shown: {{count}} accounts winding down.",
        "CLOSED_one": "Not shown: {{count}} account no longer trading.",
        "CLOSED_other": "Not shown: {{count}} accounts no longer trading."
      }
    }
  },
  "subscriptions": {
    "filter": "Filter by retailer or status",
    "title": "Subscriptions",
    "sub": "As of {{today}} ({{timezone}})",
    "filterAll": "All · {{n}}",
    "status": {
      "ACTIVE": "Active",
      "DUE_FOR_RENEWAL": "Due for renewal",
      "EXPIRED": "Expired",
      "CANCELLED": "Cancelled",
      "NONE": "No subscription"
    },
    "colRetailer": "Retailer",
    "colStatus": "Status",
    "colStarted": "Started",
    "colRenews": "Renews",
    "colFee": "Fee",
    "colPaid": "This year's fee",
    "colTerm": "Term",
    "inDays": "in {{n}} days",
    "paid": "Paid",
    "unpaid": "Unpaid",
    "emptyTitle": "No retailers in this view",
    "emptyBody": "Choose another filter, or record a term for a retailer.",
    "historyTitle": "{{name}} — subscription history",
    "expiredNote": "What an expired subscription means for ordering and held pieces has not been decided (D15, D85). Nothing is blocked.",
    "noTermsTitle": "No terms recorded",
    "noTermsBody": "Record the first term once the annual fee has been received.",
    "markPaid": "Mark fee paid",
    "reference": "Bank reference",
    "referenceHint": "The reference or receipt number the payment came with.",
    "reason": "Reason",
    "cancelTerm": "Cancel term",
    "confirmCancel": "Yes, cancel",
    "cancelledOn": "Cancelled {{date}} — {{reason}}",
    "termStart": "Starts",
    "termEnd": "Renews on",
    "fee": "Fee",
    "paidNow": "Fee already received",
    "recordTerm": "Record term",
    "changesTitle": "Changes",
    "action": {
      "subscription.term.recorded": "Term recorded",
      "subscription.fee.paid": "Fee marked paid",
      "subscription.term.cancelled": "Term cancelled"
    },
    "sb06": "The subscription fee is separate from consignment. It never appears in a retailer's payable or affects their budget (SB-06).",
    "failed": "Something went wrong. Please try again."
  },
  "retailerBudget": {
    "title": "Inventory budget",
    "assigned": "Min limit", "assignedHint": "The inventory budget this account trades against. Every order is measured against it.",
    "available": "Available to order", "inUse": "In use",
    "totalAmount": "Max limit", "totalAmountHint": "Twice the min limit, for reference only. It stops nothing \u2014 what an order is measured against is the min limit and its tolerance.",
    "availableHint": "Never below zero; at zero the account cannot order.",
    "component": "Held by", "value": "Value",
    "components": {
      "openOrders": "Open orders, not yet shipped",
      "inTransit": "Pieces in transit",
      "held": "Pieces held",
      "soldUnpaid": "Sold, not yet paid"
    },
    "release": {
      "SALE": "On this portal a sold piece frees its budget as soon as the sale is reported.",
      "PAYMENT": "On this portal a sold piece keeps its budget in use until Miraki confirms payment for it."
    },
    "tolerance": {
      "label": "Tolerance (% over budget accepted)",
      "hint": "Leave empty to use the portal's figure, {{pct}}%. Enter 0 to accept nothing over the budget. Up to four decimal places.",
      "RETAILER": "This account is allowed {{pct}}% over its available budget.",
      "PORTAL": "This account takes the portal's tolerance, {{pct}}% over its available budget.",
      "PLATFORM_DEFAULT": "No tolerance has been set for this account or this portal, so the platform default of {{pct}}% applies.",
      "ceiling": "The most it can order today is <m/>.",
      "problem": {
        "FORMAT": "Enter a percentage such as 5 or 7.5, with at most four decimal places.",
        "TOO_HIGH": "A tolerance cannot be more than 100%."
      }
    },
    "noneTitle": "No budget set: this account cannot place orders.",
    "noneBody": "Someone with budget permission needs to set one.",
    "noneBodyEdit": "Set one below to let it order.",
    "over": "In use is above the assigned budget. That is allowed, but the account cannot order until in-use value falls below it.",
    "change": "Change budget", "set": "Set budget", "save": "Save budget", "cancel": "Cancel",
    "newAmount": "New budget ({{currency}}, wholesale value)",
    "amountHint": "A decimal amount, for example 50000.00. Zero stops ordering.",
    "problem": {
      "REQUIRED": "Enter an amount.",
      "FORMAT": "Enter a decimal amount with at most four places.",
      "NEGATIVE": "A budget cannot be negative."
    },
    "reason": "Reason", "reasonHint": "Recorded with the change. 3 to 300 characters.",
    "belowInUse": "This is below what is already in use. That is allowed: the account will not be able to order until in-use value falls below it.",
    "failed": "The budget could not be saved. Please try again.",
    "traceable": "Every change is recorded with its reason under Recent activity."
  },
  "returnsDesk": {
    "title": "Returns",
    "sub": "As of {{day}}",
    "tabRequests": "Return requests", "tabStock": "Stock & losses",
    "recall": "Recall", "recalledBy": "Recalled by Miraki. The retailer cannot withdraw it, and the pieces cannot be marked sold while it stands.",
    "cancelRecall": "Cancel the recall for {{n}} piece(s) not yet back", "cancelReason": "Why the recall is cancelled",
    "cancelHint": "For instance, the retailer sold the piece before the recall reached them. The pieces stay with the retailer.",
    "emptyTitle": "No return requests",
    "emptyBody": "Retailers request returns from My Inventory. Requests appear here, waiting on a decision first.",
    "queueTitle": "{{n}} waiting on a decision",
    "stage": { "DECIDE": "To decide", "GOODS": "Awaiting goods", "DONE": "Finished" },
    "pieces": "{{n}} piece(s)",
    "countsLine": "{{requested}} to decide · {{approved}} approved, not back · {{received}} received",
    "requested": "Requested", "lastDecided": "Last decided",
    "released": "Budget released", "awaiting": "Still out",
    "remarks": "Retailer's reason:", "recallRemarks": "Miraki's reason:",
    "withdrawn": "Withdrawn by the retailer on {{day}}.",
    "budgetNotMoney": "Receiving a piece releases its wholesale value from the retailer's budget in use. It is not a credit and nothing is owed: an unsold piece was never charged for.",
    "releasedNow": "Received. Budget released:",
    "none": "Nothing",
    "colJob": "Job Number", "colValue": "Wholesale", "colDaysHeld": "Days held", "colState": "State",
    "state": {
      "REQUESTED": "To decide", "APPROVED": "Approved — not back", "REFUSED": "Refused",
      "WITHDRAWN": "Withdrawn", "RECEIVED": "Received", "SOLD_INSTEAD": "Sold instead"
    },
    "receivedOn": "Received {{day}}",
    "decideFor": "Decision for {{job}}",
    "approve": "Approve", "refuse": "Refuse", "leave": "Leave for later",
    "refusalReason": "Why {{job}} is refused",
    "reasonMissing": "Say why for each refused piece: {{jobs}}.",
    "recordDecision": "Record decision — {{approve}} approved, {{refuse}} refused",
    "decisionHint": "Pieces left alone stay waiting for a later decision.",
    "arrived": "{{job}} has arrived", "arrivedShort": "Arrived",
    "receivedOnLabel": "Arrived on", "receivedOnHint": "Leave empty for today.",
    "recordReceipt": "Record {{n}} piece(s) received",
    "willRelease": "Releases:",
    "failed": "That did not work. Please try again.",
    "refusal": {
      "REQUEST_WITHDRAWN": "The retailer has withdrawn this request.",
      "DUPLICATE_PIECE": "{{job}} appears twice.",
      "NOT_ON_REQUEST": "{{job}} is not on this request.",
      "ALREADY_DECIDED": "{{job}} has already been decided ({{state}}).",
      "REASON_REQUIRED": "Say why {{job}} is refused.",
      "NOT_HELD": "{{job}} is no longer held by the retailer ({{status}}) — it may have been sold.",
      "NOT_APPROVED": "{{job}} is not approved for return ({{state}}).",
      "RECEIVED_IN_FUTURE": "{{receivedOn}} is after today ({{today}}).",
      "RECEIVED_BEFORE_REQUEST": "{{receivedOn}} is before the request was made ({{requestedOn}}).",
      "NOT_ON_RECALL": "{{job}} is not on this recall.",
      "NOT_OUTSTANDING": "{{job}} is no longer outstanding on this recall ({{state}})."
    }
  },
  "stock": {
    "policyTitle": "This portal's rules",
    "policy": {
      "recallNONE": "Ageing triggers nothing (D20 is open). Recall any held piece by hand.",
      "recallALERT": "Pieces held {{days}} days or more are listed as due for recall. A person decides (D20).",
      "recallRECALL": "Pieces held {{days}} days or more are recalled automatically each day (D20).",
      "lossNone": "No loss outcome is offered first (D39, D81 are open) — choose one for each report.",
      "lossDefault": "{{outcome}} is offered first for a loss (D81). It is still your choice on each report.",
      "cycleNone": "No counting cycle (D40 is open). Count a retailer when you need to.",
      "cycle": "Each retailer is due a count every {{days}} days (D40).",
      "setOn": "Set on Portal settings."
    },
    "runRecalls": "Recall everything due now", "ranRecalls": "{{n}} recall(s) raised.", "ranNone": "Nothing was due.",
    "retailersTitle": "What retailers hold",
    "colRetailer": "Retailer", "colHeld": "Pieces held", "colValue": "Wholesale value", "colOldest": "Oldest (days)",
    "colDue": "Due for recall", "colLosses": "Open losses", "colCounted": "Last counted", "colCountDue": "Count due",
    "yes": "Yes", "no": "No", "never": "Never", "noCycle": "—",
    "noRetailers": "No retailer holds a piece on this portal.",
    "open": "Open",
    "heldTitle": "{{name}} should hold {{n}} piece(s)",
    "account": "Account: {{status}}",
    "colJob": "Job Number", "colPiece": "Piece", "colDays": "Days held", "colReturn": "Return",
    "due": "Due for recall", "pick": "Select {{job}}",
    "returnState": { "REQUESTED": "Return requested", "APPROVED": "Return approved", "RECALL": "Recalled" },
    "recallSelected": "Recall {{n}} piece(s)", "recallRemarks": "Why Miraki wants them back", "recallRemarksHint": "The retailer reads this on Return requests.",
    "recallSent": "Recall raised. It is on the Return requests tab, awaiting the goods.",
    "reportOne": "Report lost, damaged or stolen", "reportFor": "Report {{job}}",
    "kind": { "LOST": "Lost", "DAMAGED": "Damaged", "STOLEN": "Stolen" },
    "kindLabel": "What happened", "accountLabel": "Account of what happened", "accountHint": "At least a sentence. The outcome is decided against it.",
    "reportGoes": "The piece leaves the retailer's inventory at once and its budget is released. Its outcome is decided separately.",
    "sendReport": "Record the report", "reported": "Reported.",
    "countTitle": "Stock count", "startCount": "Produce the count list", "countStarted": "Count list produced on {{day}} — {{n}} piece(s).",
    "countOn": "Counted on", "countRemarks": "Anything not on the list", "countRemarksHint": "A piece on the shelf that is not on this list, for instance.",
    "result": { "PRESENT": "Present", "MISSING": "Missing", "DAMAGED": "Damaged" },
    "resultFor": "Result for {{job}}", "noteFor": "Note for {{job}}",
    "completeCount": "Record the count", "countRecorded": "Count recorded.", "countMissing": "Give a result for every piece: {{jobs}}.",
    "countNoMove": "Recording a count moves no piece. Report a missing or damaged piece below if it is lost.",
    "countsTitle": "Earlier counts", "countedOn": "Counted {{day}}", "discrepancies": "{{n}} discrepancy(ies)",
    "reportFromCount": "Report", "alreadyReported": "Reported",
    "lossesTitle": "Losses",
    "noLosses": "No losses reported.",
    "colKind": "Reported", "colBy": "By", "colOutcome": "Outcome",
    "by": { "RETAILER": "Retailer", "INTERNAL": "Miraki" },
    "outcome": {
      "OPEN": "Awaiting a decision", "CHARGED": "Charged to the retailer", "INSURANCE_CLAIM": "Insurance claim",
      "WRITTEN_OFF": "Written off", "FOUND": "Found"
    },
    "chooseOutcome": "Choose an outcome", "outcomeFor": "Outcome for {{job}}", "outcomeReason": "Reason",
    "recordOutcome": "Record outcome",
    "chargeWarning": "Charging adds this to the retailer's payables:",
    "failed": "That did not work. Please try again.",
    "refusal": {
      "ACCOUNT_MAY_NOT_REPORT": "This account cannot report pieces while its status is {{status}}.",
      "NOT_FOUND": "{{job}} was not found.",
      "NOT_YOURS": "{{job}} is not held by this retailer.",
      "NOT_HELD": "{{job}} is not held right now ({{status}}).",
      "ON_RETURN_REQUEST": "{{job}} is on a return request ({{state}}). Withdraw or cancel it first.",
      "NOT_ON_THIS_COUNT": "{{job}} was not on this count's list.",
      "ALREADY_REQUESTED": "{{job}} is already on a return request ({{state}}).",
      "DUPLICATE_PIECE": "{{job}} is listed twice.",
      "NOT_ON_LIST": "{{job}} is not on this count's list.",
      "NO_RESULT": "{{job}} has no result.",
      "ALREADY_COMPLETE": "This count has already been recorded.",
      "COUNTED_IN_FUTURE": "{{countedOn}} is after today.",
      "COUNTED_BEFORE_LIST": "{{countedOn}} is before the list was produced ({{listedOn}}).",
      "NOT_OPEN": "That outcome cannot be recorded from here.",
      "PIECE_MOVED": "The piece has moved since it was reported ({{status}}). Reload.",
      "COUNT_OPEN": "A count is already open for this retailer.",
      "NOTHING_HELD": "This retailer holds no pieces to count."
    }
  },
  "windDown": {
    "title": "Account wind-down",
    "listTitle": "Winding down and closed",
    "tabAll": "All",
    "status": { "ACTIVE": "Active", "ON_HOLD": "On hold", "DORMANT": "Dormant", "WIND_DOWN": "Winding down", "CLOSED": "Closed" },
    "nothing": "Nothing",
    "colAccount": "Account", "colStatus": "Status", "colSince": "Since", "colPiecesOut": "Pieces out",
    "colOpenOrders": "Open orders", "colPayable": "Payable", "colClosure": "Closure",
    "colOrder": "Order", "colOrderStatus": "Stage", "colSubmitted": "Submitted",
    "closedOn": "Closed {{day}}", "windingSince": "since {{day}}",
    "ready": "Can close", "blockedBy": "{{n}} blocking",
    "emptyTitle": "No account is winding down",
    "emptyBody": "Wind-down begins from a retailer's record. Closed accounts stay listed here.",
    "backToList": "Back to the list", "openRecord": "Open the retailer record",
    "beganFrom": "This account was {{status}} when wind-down began. Cancelling returns it to {{status}}.",
    "closedTitle": "Closed.",
    "closedBody": "Read-only. Its users are deactivated; its orders, pieces, ledger and audit history are kept and still appear in reports.",
    "whatStops": "Winding down stops new orders only. The retailer can still sign in, report sales of pieces they hold and request returns, so that every piece can be sold or returned and the account settled.",
    "openOrders": "Open orders", "piecesOut": "Pieces out", "payable": "Payable",
    "payableHint": "Below zero is a credit owed to the retailer.",
    "subscription": "Subscription", "feeUnpaid": "Fee unpaid:", "renewsOn": "Renews {{day}}",
    "logins": "Active logins",
    "outDetail": "Out:", "pieceCount": "{{n}} {{status}}",
    "piece": { "MADE": "made, not shipped", "IN_TRANSIT": "in transit", "DISCREPANCY": "in discrepancy", "HELD": "held" },
    "soldDetail": "{{n}} sold and not yet paid. What they owe is in the payable.",
    "canClose": "Nothing blocks closure.",
    "cannotClose": "This account cannot be closed yet:",
    "blocker": {
      "CLOSED": "It is already closed.",
      "NOT_WINDING_DOWN": "It is not winding down. Begin wind-down first.",
      "PIECES_OUT": "{{n}} piece(s) are still with, or on their way to, the retailer. Each must be sold or returned.",
      "LOSS_OPEN": "{{n}} lost or damaged piece(s) have no recorded outcome.",
      "PAYABLE_OWED": "A payable is outstanding.",
      "CREDIT_HELD": "The retailer holds a credit. Refund or reverse it first.",
      "OPEN_ORDERS": "{{n}} order(s) are still open and would ship pieces to this account."
    },
    "reason": "Reason", "reasonHint": "Recorded in the account's history. At least three characters.",
    "begin": "Begin wind-down", "beginHint": "Stops new orders. Nothing else changes for the retailer.",
    "cancel": "Cancel wind-down", "close": "Close account",
    "failed": "That did not work. Please try again."
  },
  "dashboard": {
    "title": "Dashboard",
    "sub": "{{portal}} · figures in {{currency}} where single-currency · as of {{today}}",
    "open": "Open", "openAmount": "Open", "none": "None",
    "notYet": "Not yet available",
    "applications": "Applications waiting", "oldest": "Oldest {{n}} days", "queueClear": "Queue clear",
    "returnsAwaiting": "Returns awaiting a decision",
    "ordersAwaiting": "Orders awaiting confirmation",
    "nearBudget": "Retailers at or near budget",
    "dormant": "No order in {{n}} days", "neverOrdered": "never ordered",
    "renewals": "Renewals due", "expired": "{{n}} expired",
    "outstanding": "Outstanding payable", "noCombined": "No combined total across currencies.",
    "overdue": "Overdue", "credits": "Credit balances held", "creditRetailers": "{{n}} retailer(s)",
    "awaitingInvoice": "Sales awaiting an invoice", "awaitingPieces": "{{n}} piece(s) · {{r}} retailer(s)",
    "awaitingSince": "oldest sold {{day}}",
    "ageing": "Unpaid invoices by age", "days": "Days", "invoices": "Invoices",
    "production": "Approved orders by production state", "stage": "State", "orders": "Orders",
    "madeVsOrdered": "Made / ordered", "pastPromised": "Past promised dispatch", "madeNotShipped": "Made, not yet shipped",
    "superAdmins": "Active Super Admins",
    "accessChanges": "Recent role and permission changes", "when": "When", "what": "What", "who": "Who",
    "noChanges": "No changes recorded",
    "retailer": "Retailer",
    "emptyTitle": "Nothing on this dashboard for your role",
    "emptyBody": "Your role does not include any of the queues or figures shown here. The menu lists the screens you can open.",
    "notShownTitle": "Not on this dashboard yet",
    "notShownBody": "These are asked for by §6.14 but have no screen to open yet, so they are not shown as figures."
  },
  "shopDashboard": {
    "title": "Dashboard",
    "sub": "{{portal}} · {{currency}} · as of {{today}}",
    "account": "Account", "seeAccount": "Account details",
    "group": "Group: {{name}}", "tier": "Pricing tier: {{tier}}", "assigned": "Assigned budget:",
    "subscription": "Subscription: {{status}}", "started": "started {{day}}", "renews": "renews {{day}}",
    "available": "Available to order", "whatHolds": "What is holding my budget", "hideBreakdown": "Hide breakdown",
    "breakdown": "Budget in use", "component": "Held by", "value": "Value",
    "inUse": { "openOrders": "Open orders", "inTransit": "Pieces in transit", "held": "Pieces you hold", "soldUnpaid": "Sold, not yet paid", "total": "Total in use" },
    "budgetNotYet": "Your budget will be shown here once budgets are live.",
    "notYet": "Not yet available",
    "openOrders": "Open orders", "seeOrders": "All orders",
    "colOrder": "Order", "colSubmitted": "Submitted", "colDispatch": "Dispatch by",
    "estimated": "(estimated)", "notConfirmed": "Not yet confirmed",
    "noOrdersTitle": "No open orders", "noOrdersBody": "Orders you place appear here until they are delivered.",
    "payable": "Outstanding payable", "nothingOwed": "Nothing owed", "seeStatement": "Statement",
    "invoices": "Recent invoices", "colInvoice": "Invoice", "colDue": "Due", "overdue": "overdue",
    "payments": "Payment history", "colReceived": "Received", "colReference": "Reference",
    "quick": "Quick access", "quickCart": "Order", "quickOrders": "Orders",
    "quickInventory": "My Inventory", "quickSold": "Sold Items",
    "onHand": "Pieces you hold", "seeInventory": "My Inventory",
    "inventory": "Inventory at wholesale and RP", "wholesale": "Wholesale", "rp": "RP",
    "byArticle": "Pieces by article", "colArticle": "Article", "colPieces": "Pieces"
  },
  "devices": {
    "title": "Trusted devices",
    "body": "Browsers that have signed in to this account with an emailed code. Each is trusted for 30 days. Revoke any you don't recognise, or one you've lost — it will be signed out within 15 minutes.",
    "colDevice": "Device",
    "colState": "Status",
    "colLastSeen": "Last used",
    "colUntil": "Trusted until",
    "thisOne": "This browser",
    "unknown": "Unknown browser",
    "revoke": "Revoke",
    "confirmRevoke": "Yes, revoke",
    "revokeFailed": "Could not revoke the device. Please try again.",
    "emptyTitle": "No trusted devices",
    "emptyBody": "Devices appear here once someone signs in to this account with an emailed code.",
    "state": { "ACTIVE": "Trusted", "EXPIRED": "Expired", "REVOKED": "Revoked" }
  },
  "paymentTerms": {
    "colStage": "Stage",
    "colWhen": "Payable",
    "colShare": "Share",
    "colCumulative": "Paid by this point",
    "colUse": "Use",
    "total": "Total",
    "gate": {
      "beginManufacture": "Before manufacturing begins",
      "markReady": "Before the order is marked ready",
      "ship": "Before shipment",
      "markDelivered": "Before delivery completes"
    }
  },
  "apply": {
    "title": "Apply for an Account",
    "lede": "Trade accounts only. Tell us about your business and we will come back to you.",
    "portal": "Applying to",
    "portalHint": "Miraki runs more than one wholesale business.",
    "businessName": "Business name",
    "firstName": "First name",
    "lastName": "Last name",
    "city": "City",
    "postalCode": "Postal code",
    "businessType": "Business type",
    "businessTypeHint": "e.g. Independent retailer, department store, online only",
    "tradeReferences": "Trade references",
    "tradeReferencesHint": "Businesses that can vouch for you. We may contact them before approving. The company name is enough; the rest helps us reach them.",
    "refCompany": "Company",
    "refContact": "Contact name",
    "refEmail": "Email",
    "refPhone": "Phone",
    "addReference": "Add another reference",
    "contactName": "Your name",
    "submit": "Submit application",
    "submitting": "Submitting…",
    "received": "Application Received",
    "referenceLede": "Your reference is <b>{{reference}}</b>. Keep it — quote it in any correspondence.",
    "whatNext": "What happens next",
    "next1": "Miraki reviews your business registration and trade references.",
    "next2": "We may come back for more information before deciding.",
    "next3": "On approval you receive a link to set a password and accept trading terms.",
    "decidedByPerson": "Applications are decided by a person, not automatically, and the decision is recorded against their name.",
    "email": "Email",
    "phone": "Phone",
    "country": "Country of registration",
    "selectCountry": "Select a country",
    "registrationFallback": "Business registration",
    "buyingGroup": "Buying group",
    "buyingGroupHint": "The buying group your business belongs to.",
    "regLooksRight": "That looks right.",
    "regHint": "Your {{country}} {{label}}. Spaces and hyphens are fine.",
    "regHintFormat": "Your {{country}} {{label}} — {{format}}. Spaces and hyphens are fine.",
    "regChooseCountry": "Choose a country first — the format depends on it.",
    "note": "Anything we should know",
    "alreadyHaveAccount": "Already have an account?",
    "documents": "Supporting documents",
    "documentsHint": "Your trade licence, business registration certificate, or photo ID of an owner. Up to {{limit}} files, up to {{mb}}MB each \u2014 PDF, JPG, PNG, WEBP, DOC or DOCX.",
    "documentsOptional": "You can submit without these. Miraki cannot approve an account until at least one has been received, so sending them now is the quickest route \u2014 and if you do not have them to hand, Miraki will ask you for them.",
    "documentsAdd": "Choose a file",
    "documentsAdding": "Uploading\u2026",
    "documentsRemaining": "{{n}} of {{limit}} sent. You can send {{remaining}} more.",
    "documentsFull": "All {{limit}} files have been sent.",
    "documentsWindow": "This upload stays open for the next hour. After that, email anything further to Miraki quoting {{reference}} and it will be added to your application.",
    "documentsClosed": "The upload window for this application has closed. Email anything further to Miraki quoting {{reference}}.",
    "documentsFailed": "That file could not be uploaded."
  },
  "kyc": {
    "title": "KYC documents",
    "ofLimit": "{{n}} of {{limit}}",
    "file": "File",
    "format": "Format",
    "size": "Size",
    "filedBy": "Filed by",
    "received": "Received",
    "download": "Download",
    "downloadDenied": "Approval rights needed",
    "add": "Attach a document",
    "adding": "Attaching\u2026",
    "accepted": "PDF, JPG, PNG, WEBP, DOC or DOCX. Up to {{limit}} per application, {{mb}}MB each. The file itself is checked, not its name.",
    "full": "This application already holds its {{limit}} documents. Remove one to attach another.",
    "noneAdmin": "Nothing has been attached yet, and an account cannot be approved without at least one document (ON-02a). The application stays open \u2014 ask the applicant for their trade licence or registration certificate, then attach it here.",
    "noneReadOnly": "No KYC documents are held against this account.",
    "emptyTitle": "No documents",
    "emptyBody": "Nothing has been attached to this application.",
    "uploadFailed": "That document could not be attached.",
    "removeFailed": "That document could not be removed.",
    "downloadFailed": "That document could not be downloaded.",
    "retention": "Held in the portal against the application, not copied onto the account. How long these are kept, and where, is an open decision (N3) \u2014 nothing deletes them today.",
    "by": {
      "APPLICANT": "Applicant",
      "RETAILER": "Retailer, after signing in",
      "MIRAKI": "Miraki"
    },
    "references": {
      "title": "Trade references",
      "promised": "Businesses the applicant says will vouch for them. The point of a reference is that somebody rings it before approving.",
      "company": "Company",
      "contact": "Contact",
      "email": "Email",
      "phone": "Phone",
      "relationship": "Relationship",
      "emptyTitle": "No trade references",
      "emptyBody": "None were given on the application."
    }
  },
  "retailerKyc": {
    "from": "Held against application {{reference}}, submitted {{on}}. Documents supplied with it stay on the application and are not copied onto this account, so what is shown here is the pack the approval was made on. Anything the retailer has filed themselves since signing in is listed with it, marked as such.",
    "noApplication": "This account has no application behind it, so there is no KYC pack to show. Accounts created before the portal, or seeded, read this way."
  },
  "shop": {
    "backToCatalog": "All products",
    "backToOrders": "Back to Orders",
    "backToInvoices": "Back to Consignment Invoices",
    "backToThisOrder": "Back to order",
    "chrome": {
      "portal": "Portal",
      "cart": "Cart"
    },
    "catalog": {
      "title": "Catalog",
      "emptyTitle": "Nothing published yet",
      "emptyBody": "Products appear here as Miraki publishes them to your portal.",
      "noMatchTitle": "Nothing matches those filters",
      "noMatchBody": "Clear a filter to see more.",
      "searchLabel": "Search code, product or collection",
      "searchPlaceholder": "Code, name, collection or your SKU…",
      "sortLabel": "Sort catalog",
      "sortNewest": "Newest first",
      "sortCollection": "By collection",
      "sortPriceLow": "Price, low to high",
      "sortPriceHigh": "Price, high to low",
      "sortCode": "By code",
      "viewGrid": "Grid",
      "viewList": "List",
      "countAll_one": "{{count}} product",
      "countAll_other": "{{count}} products",
      "countFiltered": "{{count}} of {{total}}",
      "facetCollection": "Collection",
      "facetCategory": "Category",
      "facetMetal": "Metal",
      "facetColour": "Colour",
      "colImage": "Image",
      "colVariant": "Code",
      "colProduct": "Product",
      "colCollection": "Collection",
      "colMetal": "Metal",
      "colStone": "Stone",
      "colPrice": "Wholesale",
      "colOrder": "Order",
      "colYourSku": "Your SKU",
      "yourSku": "Your SKU: {{sku}}",
      "lastViewedTitle": "Recently viewed",
      "recentClear": "Clear",
      "recentPrev": "Scroll recently viewed left",
      "recentNext": "Scroll recently viewed right"
    },
    "variant": {
      "back": "Back to catalog",
      "madeToOrderSub": "{{collection}} · {{category}} · made to order",
      "startingFrom": "Starting from",
      "optionsTitle": "Options",
      "colCode": "Code",
      "colCarat": "Carat",
      "colWeight": "Weight",
      "startingPriceNote": "Starting price — the lowest across this design's variants",
      "code": "Code",
      "specification": "Specification",
      "purity": "Purity",
      "colour": "Metal colour",
      "netWeight": "Net metal weight",
      "grossWeight": "Gross weight",
      "totalCarat": "Total carat weight",
      "price": "Wholesale price",
      "rp": "RP",
      "rpHint": "Suggested retail price. You are invoiced on the wholesale price.",
      "grams": "{{value}} g",
      "carats": "{{value}} ct",
      "millimetres": "{{value}} mm",
      "ordering": "Ordering",
      "metalTitle": "Metal",
      "stoneTitle": "Stone",
      "stoneShape": "Shape",
      "stoneCarat": "Carat",
      "stoneCut": "Cut",
      "stoneColour": "Colour",
      "stoneClarity": "Clarity",
      "stoneCount": "Stone count",
      "stoneCertified": "Certified",
      "stoneCertifiedYes": "Yes",
      "stoneCertifiedNo": "No",
      "stoneOrigin": "Origin",
      "severalTitle": "Order several",
      "severalPickFirst": "Still to choose above: {{fields}}. Every combination Miraki makes of what you pick appears here, with a quantity and a remark of its own.",
      "stoneSpec": "Piece",
      "rpPrice": "RP Price", "lineTotal": "Total price",
      "remarkColumn": "Remark",
      "severalQtyFor": "Order several: quantity for {{code}}",
      "severalSizeFor": "Order several: size for {{code}}",
      "remarkFor": "Order several: remark for {{code}}",
      "remarkRowPlaceholder": "Engraving, finish, packing…",
      "noStone": "No stone",
      "addSeveralNone": "Add to cart",
      "addSeveralPending": "Adding to cart…",
      "addSeveral_one": "Add 1 piece to cart",
      "addSeveral_other": "Add {{count}} pieces to cart",
      "severalFailed": "Nothing was added — your cart is unchanged. {{reason}}",
      "severalAdded_one": "1 piece added to your cart.",
      "severalAdded_other": "{{count}} pieces added to your cart.",
      "severalViewCart": "View cart",
      "severalEnterQty": "Enter a quantity against at least one piece to add it to your cart.",
      "stoneVariantsTitle": "Stone",
      "stoneMaterial": "Material",
      "stoneVariantSelect": "Select",
      "stoneVariantSelected": "Selected",
      "stoneVariantAny": "Any",
      "metalChoose": "Choose…",
      "stoneVariantView": "View this piece",
      "stoneVariantNoMatch": "Miraki does not make this combination. Change one of the choices above.",
      "stoneVariantMany_one": "1 piece matches — put a quantity against it below.",
      "stoneVariantMany_other": "{{count}} pieces match — put quantities against the ones you want in Order several below.",
      "metalFitTitle": "Metal & fit",
      "makeTitle": "Make & dimensions",
      "makeSetting": "Setting",
      "makeFinish": "Finish",
      "makeClasp": "Clasp",
      "makeBacking": "Backing",
      "makeHallmark": "Hallmark",
      "rhodiumLabel": "Rhodium plating",
      "rhodiumPlated": "Plated — before re-plating",
      "rhodiumNotPlated": "Not plated — {{colour}} gold",
      "imageryNote": "Nobody has photographed this piece yet — a picture appears here as soon as Admin publishes one for it.",
      "imageAlt": "Image {{n}} of {{count}}",
      "zoomOpen": "View larger",
      "zoomTitle": "Larger view",
      "zoomClose": "Close",
      "zoomPrev": "Previous photograph",
      "zoomNext": "Next photograph",
      "zoomInHint": "Click the photograph to magnify",
      "zoomOutHint": "Move to look around · click to zoom out",
      "size": "Size",
      "quantity": "Quantity",
      "remarkLabel": "Remark for this piece",
      "remarkPlaceholder": "Anything Miraki should know — engraving, finish, packing. Passed to the team with your order.",
      "alsoLikeTitle": "You may also like",
      "productionTitle": "Production & ordering",
      "madeToOrder": "Made to order",
      "madeToOrderValue": "Yes — nothing is held in stock",
      "leadTime": "Lead time",
      "leadTimeValue": "40–60 days",
      "productionBegins": "Production begins",
      "productionBeginsValue": "On Miraki's approval of your order"
    },
    "addToCart": {
      "fewer": "Fewer",
      "more": "More",
      "qtyFor": "Quantity for {{code}}",
      "add": "Add to order",
      "update": "Update",
      "added": "Added — see it on your order",
      "viewOnly": "Your sign-in can view the order but not change it",
      "inOrder": "In your order: {{quantity}}"
    },
    "order": {
      "title": "Order",
        "doorFind": "Find",
      "doorFindSub": "code or spec",
      "doorAgain": "Order again",
      "doorAgainSub": "from last time",
      "doorPaste": "Paste",
      "doorPasteSub": "a list",
      "doorGrid": "Bulk order entry",
      "doorGridSub": "type or paste a grid",
      "gridSub": "Type or paste codes and quantities. Everything is checked before it reaches your cart.",
      "gridAddRow": "Add row",
      "gridColCode": "Code",
      "gridColProduct": "Product",
      "gridColQty": "Qty",
      "gridColSize": "Size / length",
      "gridColUnit": "Unit",
      "gridColLineTotal": "Line total",
      "gridNotSized": "Not sized",
      "gridSizePlaceholder": "Choose…",
      "gridUnrecognised": "Not a recognised code",
      "gridQuantityError": "Enter a quantity",
      "gridSizeError": "Choose a size",
      "gridSummary_one": "{{count}} valid line",
      "gridSummary_other": "{{count}} valid lines",
      "gridSummaryErrors_one": "{{count}} to fix",
      "gridSummaryErrors_other": "{{count}} to fix",
      "gridReadyLabel": "ready",
      "gridGo": "Add to cart",
      "gridNote": "Unrecognised codes are named before anything is added, not discovered at checkout. A ring needs a size and a bracelet or chain needs a length before it can go in the cart. Earrings have neither and are not asked. Both lists are set by Miraki — you are choosing from them, not typing a number.",
      "findPlaceholder": "Code, product, purity or colour — try 18k, or AUR-RG",
      "findHint": "Type any part of a code or a specification. Results narrow as you type.",
      "againHint": "Everything you ordered last time, already added to your order. Change what you need and leave the rest — anything left at zero is simply not ordered.",
      "againEmptyTitle": "No previous order",
      "againEmptyBody": "Nothing to repeat yet — your first order will appear here once it is submitted.",
      "pasteHint": "One line per item: the code, then the quantity. Straight out of a spreadsheet.",
      "pastePlaceholder": "AUR-RG-18Y-050  12\nNOO-EA-18W-030  18",
      "pasteGo": "Add these to the order",
      "pasteClear": "Clear",
      "pasteAdded_one": "{{count}} line added.",
      "pasteAdded_other": "{{count}} lines added.",
      "pastePartial": "{{added}} added, {{missed}} not recognised. {{codes}} — check the code or search for it.",
      "colPiece": "Piece",
      "colPurity": "Purity",
      "colColour": "Metal colour",
      "colPrice": "Your price",
      "colQuantity": "Quantity",
      "colLineTotal": "Line total",
      "colTotalPrice": "Total price",
      "emptyTitle": "Nothing matches",
      "emptyBody": "Try a different search, or add codes from Find.",
      "removedNote_one": "{{count}} item was removed from your order — no longer available.",
      "removedNote_other": "{{count}} items were removed from your order — no longer available.",
      "barLines": "Lines",
      "barPieces": "Pieces",
      "barTotal": "Order total",
      "barCredit": "Credit position",
      "barOverLimit": "over limit",
      "barProjected": "projected",
      "barLimit": "limit",
      "clear": "Clear",
      "review_one": "Review {{count}} line",
      "review_other": "Review {{count}} lines",
      "reviewEmpty": "Review order",
      "reviewTitle": "Review & submit",
      "keepAdding": "Keep adding",
      "nothingTitle": "Nothing to review",
      "nothingBody": "Your order is empty. Add a quantity to anything in Bulk order entry, Find, Order again or Paste.",
      "backToOrder": "Back to order",
      "overLimitTitle": "This order takes the account past its credit limit.",
      "overLimitBody": "Projected {{projected}} against a limit of {{limit}}. This is shown so you know before you submit — nothing here stops you continuing.",
      "linesPieces": "{{lines}} lines · {{pieces}} pieces",
      "colRemark": "Remark",
      "remarkFor": "Remark for {{code}}",
      "remarkPlaceholder": "Engraving, finish, packing…",
      "colUnit": "Unit",
      "termsTitle": "Terms on this order",
      "orderTotal": "Order total",
      "paymentTerms": "Payment terms",
      /*
       * D75 — `net` and `prepayment` are GONE from here. The panel no longer builds its own
       * sentence from a day count: it states the rule in force through `dueRuleWords()`, under
       * `payables.dueRule.*`, because an account falling back to a MONTH_END portal has no
       * net-days reading and "Net 30" was describing a rule the portal does not operate.
       */
      "incoterm": "Incoterm",
      "accountManager": "Account manager",
      "pricesNote": "Prices — recalculated every time you open this page, not captured until Miraki prices the order",
      "whatNext": "What happens next",
      "whatNextBody": "Miraki prices it and sends it back for you to accept.",
      "submit": "Submit order",
      "submitCount": "Submit order ({{count}})",
      "submitPick": "Select lines to submit",
      "pickLinesHint": "Tick the lines you want in this order — or tick the box at the top of the table to take them all. Anything left unticked stays in your cart for later.",
      "cartTotal": "Cart total",
      "qtyFor": "Quantity for {{code}}",
      "qtyFailed": "That quantity could not be saved",
      "submitNote": "Submission isn't available yet — this lands with R-06.",
      "unpublishedTitle": "Not sold here any more",
      "unpublishedBody": "This variant is no longer published, so it is not part of your order — its line was dropped.",
      "submitFailed": "Could not submit your order",
      /*
       * Second person throughout. This is the retailer's own order screen, and the sentence read
       * "more than THIS ACCOUNT can order right now. The most YOU can place today is …" — third
       * person and then second, about the same buyer, in one refusal.
       *
       * The two voices are a real distinction and the catalog already keeps them apart by KEY:
       * `retailerBudget.tolerance.ceiling` says "the most IT can order today" because Admin is
       * being told about somebody else's account, and this one says "you" because the reader is
       * the account. Neither sentence may borrow the other's person (BG-04, OR-03).
       */
      /*
     * CR-57 / D105 — the account's KYC is not approved, so submitting was refused. Three situations,
     * three sentences, because each has a different next step; see KycRefusal. Never "that did not
     * work": the retailer has filled a cart and will ring their account manager.
     */
    "kyc": {
      "cartSaved": "Your cart is saved.",
      "needed": {
        "title": "Verify your business to place this order.",
        "body": "Miraki checks every business before its first order. Send your trade licence and proof of identity on your Account page, and you can place this order once they are approved.",
        "link": "Send your documents"
      },
      "reviewing": {
        "title": "Your documents are being reviewed.",
        "body": "Miraki has your business documents and is checking them. You will be able to place this order once they are approved. There is nothing you need to do.",
        "link": "See what you sent"
      },
      "rejected": {
        "title": "Miraki could not accept your business documents.",
        "lead": "This is what to fix:",
        "noReason": "No reason was recorded. Please contact your account manager.",
        "link": "Update your documents"
      }
    },
    "overBudget": "This order is more than you can order right now. The most you can place today is <m/>. Remove a line or lower a quantity, or ask Miraki to review your inventory budget.",
      "addFailed": "Those lines could not be added to your cart. Some may have been added already — check your cart before trying again.",
      "selectAll": "Select all",
      "selectNone": "Clear selection",
      "selectLine": "Select {{name}}",
      "selectedCount_one": "{{count}} line selected",
      "selectedCount_other": "{{count}} lines selected",
      "removeSelected": "Remove selected",
      "removeFailed": "Could not remove the selected lines from your cart"
    },
    /* D97 — the DESPATCH document's list, retailer side (R-23). */
    "consignmentInvoices": {
      "emptyTitle": "No consignment invoices yet",
      "emptyBody": "When Miraki invoices the pieces that are ready, the invoice appears here with its shipment.",
      "colInvoice": "Invoice",
      "colOrders": "Orders",
      "colShipment": "Shipment",
      "noShipment": "Not shipped yet"
    },
    "reconciliation": {
      "title": "What happened to these pieces",
      "note": "Nothing is payable for consigned goods. A piece becomes payable once you report it sold.",
      "notDespatched": "Not yet despatched",
      "notDespatchedBody": "This delivery has no shipment yet, so there are no pieces to show.",
      "colJob": "Job No",
      "colItem": "Item",
      "colValue": "Value",
      "colStatus": "Status",
      "colInvoice": "Invoice",
      "colOutstanding": "Outstanding",
      "nothingDue": "Nothing due",
      "notInvoiced": "Not invoiced yet",
      "consigned": "Consigned",
      "incoming": "On the way",
      "held": "On hand",
      "sold": "Sold",
      "returned": "Returned",
      "other": "Lost or written off",
      "invoiced": "Invoiced",
      "outstanding": "Outstanding",
      "bucketIncoming": "On the way",
      "bucketHeld": "On hand",
      "bucketSold": "Sold",
      "bucketReturned": "Returned",
      "bucketOther": "Other",
      "soldOn": "Sold {{date}}",
      "returnedOn": "Returned {{date}}",
      "viewInvoice": "View invoice"
    },
    /*
     * D97 — the DESPATCH document, retailer side (R-26, and the panel on R-07).
     *
     * `statusOPEN` and `statusSHIPMENT_CREATED` below are copied forward so WS-D's migration
     * is a pure key-prefix change, but they are SUPERSEDED by the top-level
     * `consignmentInvoice.state.*` block: a screen must render a state label as
     * t(`consignmentInvoice.state.${state}`) and never from a map keyed by the union, so that
     * SH-14's terminal state (D92, open) is one line in `packages/contracts` plus one key
     * here and no screen edit at all.
     */
    "consignmentInvoice": {
      "panelTitle": "Consignment invoice",
      "loading": "Loading Consignment Invoice...",
      "loadFailed": "We could not load the Consignment Invoice. Please try again in a moment.",
      "none": "No Consignment Invoice has been created for this order yet.",
      "colNumber": "Consignment invoice",
      "colCreated": "Created",
      "colStatus": "Status",
      "colShipment": "Shipment",
      "noShipmentShort": "Not created yet",
      "back": "Back to orders",
      "createdOn": "Created {{date}}",
      "statusOPEN": "Open",
      "statusSHIPMENT_CREATED": "Shipment created",
      "ordersTitle": "Orders and products",
      "colOrder": "Order",
      "colProduct": "Product",
      "colQty": "Qty",
      "colUnitPrice": "Unit price",
      "colAmount": "Amount",
      "orderTotal": "Order total",
      "expand": "Show products",
      "collapse": "Hide products",
      "courierCharged": "Courier charge",
      "shipmentTitle": "Shipment",
      "noShipment": "Shipment has not been created yet.",
      "shipmentReference": "Shipment",
      "courierName": "Courier",
      "trackingNumber": "Tracking number",
      "noTracking": "Not available yet",
      "copy": "Copy",
      "copied": "Copied",
      "copyFailed": "Could not copy",
      "dispatchedLabel": "Dispatched",
      "deliveredLabel": "Delivered",
      "readyToSend": "Payment received. Your shipment is packed and will be dispatched by Miraki shortly.",
      "awaitingPayment": "Your shipment is packed. It will be sent once the remaining payment has been received.",
      "shipmentStatus": "Shipment status"
    },
    "orders": {
      "title": "Orders",
      "shipped": "Shipped",
      "delivered": "Delivered",
      "acknowledge": "Confirm Receipt",
      "acknowledging": "Confirming…",
      "acknowledged": "Receipt confirmed.",
      "acknowledgeFailed": "We couldn't confirm receipt. Please try again.",
      "confirmTitle": "Confirm receipt",
      "confirmBody": "Have you received this shipment? Confirming will mark the order as Delivered.",
      "confirmYes": "Yes, I received it",
      "confirmNo": "Not yet",
      "awaitingNote": "Waiting for you to confirm the shipment has arrived.",
      "timelineLabel": "Shipment progress",
      "stepConfirmed": "Order Confirmed",
      "stepAwaiting": "Awaiting Receipt Confirmation",
      "editOrder": "Edit Order",
      "editTitle": "Edit order {{ref}}",
      "editHint": "Change the pieces or size straight in the table below, then send the change for approval. Your order stays exactly as confirmed until Miraki approves it.",
      "editIntro": "Change the quantity or size of a line. Your order stays exactly as confirmed until Miraki approves the change.",
      "editColItem": "Item",
      "editColVariant": "Variant",
      "editColQty": "Pieces",
      "editColSize": "Size",
      "editReview": "Review changes",
      "editBack": "Back",
      "editNothing": "Nothing has changed yet.",
      "editSummaryTitle": "Please check these changes",
      "editSummaryLine": "{{name}}: {{what}}",
      "editQtyChange": "pieces {{from}} → {{to}}",
      "editSizeChange": "size {{from}} → {{to}}",
      "editNewTotal": "Order total if approved",
      "editCurrentTotal": "Current order total",
      "editSubmit": "Send for approval",
      "editSending": "Sending…",
      "editSent": "Your change request was sent to Miraki. The order stays as confirmed until it is approved.",
      "editFailed": "We couldn't send the request. Please try again.",
      "editNoSize": "no size",
      "changesTitle": "Change requests",
      "changePending": "Pending approval",
      "changeApproved": "Approved",
      "changeRejected": "Rejected",
      "changeRejectedWhy": "Reason: {{reason}}",
      "changeRequestedAt": "Requested {{when}}",
      "changeDecidedAt": "Decided {{when}}",
      "emptyTitle": "No orders yet",
      "emptyBody": "Submit your cart from Bulk order entry to see it here.",
      "colOrder": "Order",
      "colStatus": "Status",
      "colSubmitted": "Submitted",
      "colPrice": "Price",
      "colDelivery": "Expected delivery",
      "colOrderNo": "order no.",
      "colItems": "Item Pcs",
      "colItemName": "Item Name",
      "colPayment": "Payment",
      "colBalance": "Balance due",
      "colContact": "Order contact",
      "colUpdated": "Last updated",
      "colShipment": "Shipment / tracking",
      "colDelivered": "Delivered",
      "colActions": "Actions",
      "expectedOn": "Expected {{date}}",
      "payment": {
        "NOT_APPLICABLE": "Not applicable",
        "UNPAID": "Unpaid",
        "PENDING_VERIFICATION": "Awaiting verification",
        "PARTIAL": "Part paid",
        "PAID": "Paid"
      },
      /* D97/A1 — the single highest-risk string in the rename. An unqualified button labelled
         "Invoice" that hands a retailer the DESPATCH document is the defect being fixed: the
         document that asks a retailer for money is the PURCHASE invoice, and this is not it. */
      "invoiceDownload": "Consignment invoice",
      "invoiceDownloadFor": "Download consignment invoice {{reference}}",
      "invoiceNone": "No consignment invoice has been issued for this order yet",
      "invoiceFailed": "The consignment invoice could not be downloaded",
      "reorder": "Reorder",
      "reorderFor": "Add the pieces of order {{reference}} to your cart",
      "reordering": "Adding…",
      /* A plural set: a single form reading "1 pieces from ORD-114 added to your cart." was the
       * same defect as "1 days", found by the rule rather than by a reader. */
      "reorderDone_one": "1 piece from {{reference}} added to your cart.",
      "reorderDone_other": "{{count}} pieces from {{reference}} added to your cart.",
      "reorderSkipped": "{{count}} no longer available and left out: {{codes}}.",
      "reorderNothing": "None of the pieces in {{reference}} can be ordered any more.",
      "reorderFailed": "The order could not be added to your cart",
      "viewCart": "View cart",
      "statusFilterLabel": "Filter by status",
      "allStatuses": "All statuses",
      "noMatchTitle": "No orders match these filters",
      "noMatchBody": "Change or clear a filter to see more orders.",
      "searchLabel": "Search by order number or item",
      "searchPlaceholder": "Order number or item",
      "paymentFilterLabel": "Filter by payment status",
      "allPayments": "All payments",
      "fromLabel": "From",
      "toLabel": "To",
      "dateFromLabel": "Ordered from",
      "dateToLabel": "Ordered to",
      "clearFilters": "Clear filters",
      "showingCount": "{{shown}} of {{total}} orders",
      "submittedOn": "Submitted {{date}}",
      "linesCount_one": "{{count}} line",
      "linesCount_other": "{{count}} lines",
      "actionFailed": "That didn't work",
      "decisionTitle": "Your decision",
      "decisionBody": "Miraki has priced this order and is waiting for you to accept it, send feedback, or decline it.",
      "accept": "Accept",
      "decline": "Decline",
      "sendFeedback": "Send feedback",
      "feedbackLabel": "What should change?",
      "feedbackHint": "Your feedback goes to Miraki and the order returns to review, so they can revise it and send it back to you.",
      "sendFeedbackConfirm": "Send feedback",
      "declineReasonLabel": "Reason for declining (optional)",
      "declineConfirm": "Decline this order",
      "declineHint": "Declining cancels this order. This cannot be undone.",
      "declinedReason": "Reason given: {{reason}}",
      "revisionPending": "You sent this order back with feedback. Miraki is revising it and will send it to you again.",

      "paymentsTitle": "Payment",
      "addPayment": "Submit a payment proof",
      "noPayments": "No payment proof submitted yet.",
      "viewFile": "View file",
      "amount": "Amount",
      "paidOn": "Date paid",
      "bankReference": "Bank reference",
      "file": "Proof (image or PDF)",
      "submitPayment": "Submit",
      "chatTitle": "Conversation with Miraki",
      "chatEmpty": "No messages yet. Ask anything about this order here — your account manager's replies appear in the same place.",
      "chatPlaceholder": "Write to Miraki — Enter to send, Shift+Enter for a new line",
      "chatSend": "Send",
      "chatSending": "Sending…",
      "chatClosed": "This order is finished. The conversation stays here to read.",
      "chatNoPermission": "Your role can read this conversation but not write in it.",
      "chatFailed": "Your message was not sent. Try again."
    },
    "statement": {
      "title": "Statement",
        "emptyTitle": "Nothing yet",
      "colDate": "Date",
      "colType": "Type",
      "colReference": "Reference",
      "colDebit": "Debit",
      "colCredit": "Credit",
      "colBalance": "Balance",
      "closingNote": "The closing balance is derived from the entries above every time this page is opened rather than stored anywhere (FN-03). A correction appears as a new reversing line, never as an edit to an old one."
    },
    /*
     * CR-57 / D105 — the retailer's own KYC pack, on R-18. KYC gates the ORDER, not the account: a
     * retailer can browse and fill a cart while this is open, and is refused only at submit. The
     * rejection copy leads with Miraki's reason, verbatim, because a retailer who does not know which
     * file was wrong uploads the same one again and rings their account manager.
     */
    "kyc": {
      "title": "Business verification",
      "intro": "Miraki verifies every business before it can place an order. Send your trade licence and proof of identity. You can browse the catalogue and fill your cart while you wait.",
      "status": {
        "NEEDED": "Documents needed", "REVIEW": "Awaiting review", "APPROVED": "Approved", "REJECTED": "Not accepted"
      },
      "reviewBody": "Miraki is reviewing what you sent. You will be able to place an order once it is approved.",
      "approvedBody": "Your business has been verified. To change these documents, contact your Miraki account manager.",
      "rejectedTitle": "Miraki could not accept your documents",
      "rejectedLead": "This is what to fix:",
      "earlierNote": "Miraki's note from the last review:",
      "noReason": "No reason was recorded. Please contact your account manager.",
      "ownerOnly": "Only the account owner can send these documents. Ask them to do it from this page.",
      "none": "Nothing sent yet.",
      "colFile": "File", "colSize": "Size", "colSent": "Sent",
      "remove": "Remove",
      "removing": "Removing…",
      "choose": "Choose a file",
      "sending": "Sending…",
      "limits": "PDF, JPEG, PNG, WebP or Word. Up to {{max}} files, {{mb}}MB each. You have sent {{count}}.",
      "limitReached": "You have sent as many as an account can hold. Remove one to send another.",
      "removeFailed": "That document could not be removed.",
      "loadFailed": "Your documents could not be loaded."
    },
    "account": {
      "onHoldTitle": "This account is on hold.",
      "onHoldBody": "New orders are refused at submission until it clears. Your account manager can tell you what is outstanding.",
      "company": "Company",
      "legalName": "Legal name",
      "country": "Country",
      "tradingCurrency": "Trading currency",
      "terms": "Trading terms",
      "paymentTerms": "Payment terms",
      /* D75 — `net` and `prepayment` removed here too; see `shop.order` above and
         `payables.dueRule.*`, which is now the only place a due rule becomes words. */
      "advance": "Advance required",
      "creditLimit": "Credit limit",
      "incoterm": "Incoterm",
      "accountManager": "Account manager",
      "rolePrimaryShort": "Primary contact",
      "registeredAddress": "Registered address",
      "noRegistered": "No registered address is on file. Add the address on your business registration.",
      "creditLimitUnset": "Not set yet",
      "billingAddress": "Billing address",
      "editAddress": "Edit",
      "addAddress": "Add",
      "addressPending": "Pending approval",
      "addressApproved": "Approved",
      "addressRejected": "Rejected",
      "addressPendingNote": "Your requested change is waiting for Miraki. The address above stays in use until it is approved.",
      "addressRejectedNote": "Miraki did not approve your last change. {{reason}}",
      "addressProposed": "Requested",
      "addressEditTitle": "Request an address change",
      "addressEditIntro": "Miraki reviews address changes before they take effect. Your current address stays in use until then.",
      "addrLine1": "Address line 1",
      "addrLine2": "Address line 2",
      "addrCity": "City",
      "addrPostal": "Postal code",
      "addrCountry": "Country code",
      "addrContact": "Contact name",
      "addrPhone": "Contact phone",
      "addrNeedsLine1City": "An address needs at least a first line and a city.",
      "addrCountryHint": "Two letters, for example AU",
      "addrSubmit": "Send for approval",
      "addrSending": "Sending…",
      "addrSent": "Sent. Your address stays as it is until Miraki approves the change.",
      "addrFailed": "We couldn't send the request. Please try again.",
      "deliveryAddress": "Delivery address",
      "deliveryAddresses": "Delivery addresses",
      "defaultAddress": "Default",
      "noBilling": "No billing address is on file. Your account manager can add one.",
      "noDelivery": "No delivery address is on file. Your account manager can add one.",
      "attention": "Attn: {{name}}",
      "managerCard": "Your account manager",
      "managerNone": "No account manager has been assigned yet.",
      "managerNoContact": "No contact details are on file for them yet.",
      "phone": "Phone",
      "status": "Status",
      "teamActive": "Active",
      "teamLocked": "Managed by Miraki",
      "teamAdd": "Add team member",
      "teamAddTitle": "Add a team member",
      "teamEditTitle": "Edit team member",
      "teamAddIntro": "They get a temporary password to sign in with, which they must replace at their first sign-in. You will see it once.",
      "teamFirstName": "First name",
      "teamLastName": "Last name",
      "teamEmailFixed": "The sign-in email can't be changed. Deactivate this login and add a new one instead.",
      "teamEdit": "Edit",
      "teamDeactivate": "Deactivate",
      "teamReactivate": "Reactivate",
      "teamResetPassword": "New password",
      "teamCreate": "Add member",
      "teamSave": "Save changes",
      "teamSaving": "Saving…",
      "teamSaved": "Saved the details for {{name}}.",
      "teamDeactivated": "{{name}} can no longer sign in.",
      "teamReactivated": "{{name}} can sign in again.",
      "teamFailed": "That didn't work. Check the details and try again.",
      "teamLoadFailed": "The team could not be loaded.",
      "teamOwnerNote": "The account owner and the primary contact can only be changed by Miraki. The Owner role cannot be given from here.",
      "teamPasswordTitle": "Temporary password",
      "teamPasswordBody": "Give this temporary password to {{email}}. It is shown only now.",
      "teamPasswordNote": "They will be asked to choose their own password the first time they sign in. If it is lost, use \"New password\".",
      "teamPasswordDone": "I've passed it on",
      "team": "Team members",
      "teamEmpty": "No other team members on this account.",
      "teamYou": "you",
      "teamInactive": "Inactive",
      "teamNoRole": "No role",
      "noTermsTitle": "No trading terms are agreed yet.",
      "noTermsBody": "Your account manager sets these; until they do, orders are handled case by case.",
      "yourSignIn": "Your sign-in",
      "name": "Name",
      "email": "Email",
      "role": "Role",
      "rolePrimary": "Primary contact — may add colleagues once R-19 is built",
      "roleUser": "User",
      "termsNotAccepted": "Trading terms have not been accepted on this account (ON-12). That screen, P-06, lands with the endpoint that records the acceptance."
    }
  },
  /*
   * The consignment invoice's states, as labels — D92's landing strip.
   *
   * The values are declared ONCE, as `consignmentInvoiceStates` in
   * `packages/contracts/src/consignment-invoice.ts`, which the drizzle schema and
   * `apps/web/src/api/types.ts` both read. A screen renders a state as
   * t(`consignmentInvoice.state.${state}`) and holds no label map of its own, so adding SH-14's
   * terminal state (D92, still open) is one line in `contracts` plus one key here.
   *
   * `CANCELLED` is included even though the retailer screens exclude it: Admin (A-19) shows it,
   * and a label block covering only the states one screen reaches is how a key path ends up
   * rendered to a customer.
   */
  "consignmentInvoice": {
    "state": {
      "OPEN": "Open",
      "SHIPMENT_CREATED": "Shipment created",
      "CANCELLED": "Cancelled"
    }
  },
  /*
   * The money block on a Consignment Invoice, shared by Admin (A-18) and the retailer (R-23) so the
   * two screens cannot word the same figure differently — they used to, and by 12,720.
   * SH-06: the goods are consigned, title stays with Miraki, so nothing is payable on a despatch. The
   * courier charge SH-13 once allowed for was retired on 04-Nov-2026 (client direction).
   */
  "despatch": {
    "panelTitle": "Value on this despatch",
    "consignedValue": "Value consigned",
    "titleNote": "These goods are consigned: title stays with Miraki until a piece is reported sold, and the value above is not payable. Nothing is payable on a despatch. A piece becomes payable on the purchase invoice raised after it sells.",
    "tax": "Tax",
    "taxNote": "Not applicable",
    "payable": "Payable on this despatch",
    "nothingPayable": "Nothing payable",
    "paid": "Paid",
    "stillToPay": "Still to pay",
    "credit": "Credit",
    "goodsPayments": "Goods payments held on these orders",
    "goodsPaymentsNote": "Received against these orders under the staged-payment arrangement retired at v1.6. Nothing on a consignment is settled by it, so it is shown apart from the payable rather than dropped.",
    "statusNOT_PAYABLE": "Nothing payable",
    "statusPENDING": "Courier charge due",
    "statusPART_PAID": "Courier charge part paid",
    "statusPAID": "Courier charge paid",
    "colConsigned": "Value consigned",
  },
  "retailerStock": {
    "title": "Stock held",
    "onHand": "Pieces on hand", "wholesale": "Wholesale value", "rp": "RP value",
    "none": "This account holds nothing at the moment.",
    "colPiece": "Piece", "colWholesale": "Wholesale", "colDays": "Days held",
    "days": "{{n}} days"
  },
  "bell": {
    "title": "Notifications",
    "none": "Notifications",
    "unread": "Notifications, {{n}} unread",
    "empty": "Nothing yet. You will see renewal reminders and dispatch-date changes here."
  },
  "myInventory": {
    "title": "My Inventory",
    "notYet": "Your inventory will appear here once consignment is live.",
    "available": "Available to order", "ofAssigned": "of {{amount}} assigned",
    "inUse": { "openOrders": "Open orders", "inTransit": "Pieces in transit", "held": "Pieces you hold", "soldUnpaid": "Sold, not yet paid" },
    "wholesale": "Wholesale value", "rp": "RP value", "onHand": "Pieces on hand",
    "held": "Pieces you hold",
    "search": "Search", "searchHint": "Job Number or product name",
    "markSold": "Mark as sold", "markSelected": "Mark {{n}} as sold", "returnSelected": "Request return of {{n}}", "clear": "Clear selection",
    "pick": "Select {{job}}",
    "colPiece": "Piece", "colWholesale": "Wholesale", "colRp": "RP", "colDays": "Days held", "colState": "State",
    "emptyTitle": "No pieces held", "emptyBody": "Pieces appear here once a shipment you received is confirmed.", "noMatch": "No piece matches that search",
    "close": "Close",
    "jobNumber": "Job Number", "goldColour": "Gold colour", "ringSize": "Ring size", "received": "Date received",
    "daysHeld": "Held", "daysMonths": "{{days}} days (about {{months}} months)",
    "onTheWay": "On the way to you", "onTheWayNote": "Confirm receipt on the order when these arrive. Days held start from the date received.",
    "reported": "Reported lost or damaged", "reportedNote": "Miraki records the outcome of each report.",
    "state": { "IN_TRANSIT": "In transit", "DISCREPANCY": "Discrepancy reported", "LOST": "Reported lost", "DAMAGED": "Reported damaged" },
    "confirmTitle": "Mark {{n}} piece(s) as sold",
    "becomesPayable": "This wholesale value becomes payable:",
    "saleDate": "Sale date", "saleDateHint": "Today, or up to 7 days earlier.",
    "cannotUndo": "A reported sale cannot be undone from here. If it was a mistake, contact Miraki before it is invoiced.",
    "cancel": "Cancel", "confirm": "Confirm sale", "failed": "That did not work. Please try again.",
    "returnTitle": "Return request: {{n}} piece(s)", "returnValue": "Wholesale value of these pieces:",
    "remarks": "Remarks", "remarksHint": "Why you are returning them.",
    "returnStaysHeld": "These pieces stay in your inventory, and in your budget, until Miraki receives them.",
    "submitReturn": "Send request",
    "returnNote": "Note (optional)", "returnNoteFor": "Note for {{job}}",
    "remarksRequired": "Say why you are returning them — at least 3 characters.",
    "returnFailed": "The request could not be sent.",
    "alreadyOnReturn": "Already on a return request: {{jobs}}.",
    "onReturn": { "REQUESTED": "Return requested", "APPROVED": "Return approved — send it back", "RECALLED": "Recalled by Miraki — send it back" },
    "reportLoss": "Report lost, damaged or stolen",
    "lossTitle": "Report {{job}}", "lossKind": "What happened",
    "lossKinds": { "LOST": "Lost", "DAMAGED": "Damaged", "STOLEN": "Stolen" },
    "lossAccount": "What happened", "lossAccountHint": "At least a sentence: when, where, and anything reported to the police or an insurer.",
    "lossGoes": "The piece leaves your inventory as soon as you send this. Miraki then decides what happens: it may be charged to you at its wholesale value, claimed on insurance, or written off. Its wholesale value:",
    "sendLoss": "Send report", "lossFailed": "The report could not be sent.",
    "lossOnReturn": "This piece is on a return request. Withdraw the request first, or tell Miraki.",
    "reportsTitle": "Lost, damaged or stolen",
    "colReported": "Reported", "colOutcome": "Outcome",
    "lossOutcome": {
      "OPEN": "Awaiting Miraki's decision", "CHARGED": "Charged at wholesale value — on your payables",
      "INSURANCE_CLAIM": "Insurance claim", "WRITTEN_OFF": "Written off — nothing to pay", "FOUND": "Found — back in your inventory"
    }
  },
  "returns": {
    "title": "Return requests",
    "sub": "As of {{asOf}}",
    "fromInventory": "Choose pieces in My Inventory",
    "open": "Open requests", "openHint": "With pieces still requested or approved",
    "releasedToDate": "Returned to your budget", "releasedHint": "Value of pieces Miraki has received back",
    "nothingYet": "Nothing yet",
    "staysHeld": "A piece stays in your inventory, and in your budget, until Miraki receives it — even once it is approved. Returning a piece frees budget; it is not a payment or a credit.",
    "empty": "No return requests", "emptyBody": "Choose the pieces you want to send back in My Inventory, and say why.",
    "requestedOn": "Requested {{day}}", "remarks": "Why:",
    "recalledOn": "Recalled by Miraki {{day}}", "recallNote": "Miraki has asked for these pieces back. Please send them; they stay in your inventory until received, and cannot be marked sold meanwhile.",
    "withdraw": "Withdraw request", "withdrawn": "Request withdrawn. The pieces stay in your inventory.",
    "withdrawnOn": "withdrawn {{day}}", "failed": "That did not work.",
    "withdrawRefused": {
      "ALREADY_DECIDED": "Miraki has already decided on a piece in this request, so it can no longer be withdrawn.",
      "ALREADY_WITHDRAWN": "This request has already been withdrawn.",
      "NOTHING_TO_WITHDRAW": "Every piece on this request has been sold instead; there is nothing left to withdraw.",
      "RETURN_NOT_FOUND": "That request was not found."
    },
    "colJob": "Job Number", "colPiece": "Piece", "colWholesale": "Wholesale", "colDays": "Days held", "colState": "Status",
    "state": {
      "REQUESTED": "Requested", "APPROVED": "Approved — send it back", "REFUSED": "Refused",
      "WITHDRAWN": "Withdrawn", "RECEIVED": "Received", "SOLD_INSTEAD": "Sold instead"
    },
    "count": {
      "REQUESTED_one": "{{count}} requested", "REQUESTED_other": "{{count}} requested",
      "APPROVED_one": "{{count}} approved", "APPROVED_other": "{{count}} approved",
      "REFUSED_one": "{{count}} refused", "REFUSED_other": "{{count}} refused",
      "WITHDRAWN_one": "{{count}} withdrawn", "WITHDRAWN_other": "{{count}} withdrawn",
      "RECEIVED_one": "{{count}} received", "RECEIVED_other": "{{count}} received",
      "SOLD_INSTEAD_one": "{{count}} sold instead", "SOLD_INSTEAD_other": "{{count}} sold instead"
    },
    "receivedOn": "received {{day}}",
    "returnedToBudget": "Returned to your budget:", "stillWithYou": "still with you:",
    "refusal": {
      "ACCOUNT_MAY_NOT_REQUEST_RETURN": "This account cannot request returns while its status is {{status}}.",
      "DUPLICATE_PIECE": "{{job}} is listed twice.",
      "NOTE_FOR_A_PIECE_NOT_REQUESTED": "A note was given for a piece not in this request.",
      "NOT_FOUND": "{{job}} was not found.",
      "NOT_YOURS": "{{job}} is not held by this account.",
      "NOT_HELD": "{{job}} is not held right now ({{status}}).",
      "ALREADY_REQUESTED": "{{job}} is already on a return request ({{state}})."
    }
  },
  "soldItems": {
    "title": "Sold Items",
    "notYet": "Your sold items will appear here once consignment is live.",
    "history": "Sold", "best": "Your best sellers",
    "bestNote": "Pieces sold per product, and the average days each was held before it sold.",
    "colPiece": "Piece", "colColour": "Gold colour", "colSoldOn": "Sold on", "colDays": "Days to sale", "colWholesale": "Wholesale",
    "colProduct": "Product", "colCount": "Sold", "colAvgDays": "Avg days to sale",
    "state": { "SOLD": "Sold", "INVOICED": "Invoiced", "PAID": "Paid" },
    "reorder": "Find in catalogue",
    "emptyTitle": "Nothing sold yet", "emptyBody": "Pieces you mark as sold in My Inventory appear here."
  },
  "payablesDesk": {
    "title": "Sales & payables",
    "asOf": "{{portal}} · as of {{asOf}}",
    "notYet": "This desk fills once consignment is live.",
    "outstanding": "Outstanding", "outstandingHint": "Sold pieces not yet paid for, invoiced or not.",
    /*
     * D75 — "the due date on the invoice", not "this portal's payment rule". A-40 is portal-wide
     * and the invoices under this tile are now dated by several rules: each account's own term
     * where it has one, the portal's where it does not. Naming the portal's rule over a mixed
     * list states a law that does not hold for every row in it.
     */
    "overdue": "Overdue", "overdueHint": "Past the due date recorded on the invoice.",
    "invoicedOpen": "Invoiced, unpaid",
    "none": "Nothing outstanding",
    "creditOf": "In credit:",
    "credits": "Retailers in credit", "creditsHint": "An over-payment or a reversal. Shown as a credit, never as a smaller debt.",
    "ageingNote": "Open balances by how long ago the invoice was issued.",
    "colBand": "Age", "colInvoices": "Invoices", "colOpen": "Open",
    "tabSold": "Awaiting invoice", "tabInvoices": "Open invoices", "tabRetailers": "By retailer",
    "awaiting": "Sales awaiting an invoice", "awaitingHint": "Pieces reported sold that no invoice covers yet.",
    "soldNote": "Select pieces for one retailer to record the invoice that covers them.",
    "select": "Select {{job}}",
    "colJob": "Job Number", "colPiece": "Piece", "colRetailer": "Retailer", "colSoldOn": "Sold on", "colWholesale": "Wholesale",
    "soldEmpty": "Nothing awaiting an invoice", "soldEmptyBody": "Pieces appear here when a retailer reports a sale.",
    "recordInvoice": "Record invoice",
    "colReference": "Reference", "colIssued": "Issued", "colDue": "Due", "colAmount": "Amount", "colAge": "Age", "colState": "State",
    /*
     * FN-04's invoice states, read by t(`payablesDesk.state.${i.state}`). Keyed by the enum on
     * `InvoiceRow`, and every value needs a key: a dynamic lookup with no key renders its own
     * PATH, which is how "payablesDesk.state.ISSUED" reached the State column.
     */
    "state": {
      "ISSUED": "Issued",
      "PART_PAID": "Part paid",
      "PAID": "Paid",
      "CANCELLED": "Cancelled"
    },
    /** Why a selection of sold pieces cannot become one invoice — `invoiceSelection`'s reasons. */
    "selection": {
      "NONE": "Select the pieces this invoice covers.",
      "RETAILERS": "An invoice covers one retailer. Those pieces belong to more than one.",
      "CURRENCIES": "An invoice is in one currency. Those pieces are valued in more than one."
    },
    /*
     * `{{count}}`, not `{{n}}`. Both call sites pass i18next's own `count` option — which drives
     * plural selection AND is the variable the string interpolates — so `{{n}}` was substituted
     * by nothing and the brace pair reached the screen: "{{n}} days" above an ageing band, and
     * "This invoice covers {{n}} piece(s)" as a dialog heading. A placeholder is only ever the
     * name its caller passes.
     */
    /*
     * Both readings of an age, and both are plural SETS. i18next picks `_one` or `_other` from
     * the same `count` it interpolates (JSON v4, `Intl.PluralRules`), so English's own rule is
     * declared here rather than approximated: a single form holding `{{count}}` uses the plural
     * selector and then ignores it, which is how an invoice one day old read "1 days".
     */
    "ageDays_one": "{{count}} day", "ageDays_other": "{{count}} days",
    /*
     * `{{band}}` — the BAND, not the word on its own. The ageing table's first column is the
     * bucket's range ("0–30 days"), and dropping the placeholder left every row of A-40's Open
     * invoices headed by a bare "days", naming no range at all. The caller has always passed
     * `band`; the string had stopped asking for it.
     */
    "days": "{{band}} days",
    "overdueChip": "Overdue",
    "invoicesEmpty": "No open invoices", "invoicesEmptyBody": "An invoice appears here until it is paid in full.",
    /* CR-56 P4 — retailers' claims to have paid a purchase invoice, awaiting Admin's decision. */
    "claims": {
      "title": "Payments to verify ({{count}})",
      "note": "Retailers say they have paid these. They are claims, not payments: nothing is counted as paid until you verify it on the invoice.",
      "colClaimed": "Claimed", "colSubmitted": "Sent", "review": "Review"
    },
    "confirmPayment": "Confirm payment",
    "colOutstanding": "Outstanding",
    "retailersNote": "Each retailer's position, in the currency the pieces were valued in.",
    "retailersEmpty": "No retailer owes anything", "retailersEmptyBody": "A position appears here once a sale is reported.",
    "reverse": "Reverse sale",
    "invoiceRecorded": "Invoice {{reference}} recorded.",
    "paymentConfirmed": "Payment confirmed.",
    "saleReversed": "The sale of {{job}} has been reversed.",
    "failed": "That did not work. Nothing has been recorded.",
    "form": {
      "invoiceTitle": "Record invoice — {{retailer}}",
      /*
       * A plural SET, not "piece(s)". "(s)" is not an English plural, it is a note to the reader
       * asking them to inflect the noun themselves — on a dialog that is about to create an
       * accounting document. i18next already picks the form from `count`, and the catalog already
       * does this where someone remembered (`shop.order.selectedCount_*`).
       */
      "covers_one": "This invoice covers 1 piece:",
      "covers_other": "This invoice covers {{count}} pieces:",
      "subtotalNote": "Miraki checks this total against the pieces before the invoice is saved.",
      "reference": "Invoice reference", "referenceHint": "The number from your accounting system.",
      "referenceRequired": "Enter the invoice reference.", "referenceLong": "That reference is too long (60 characters at most).",
      "issuedOn": "Issue date", "issuedRequired": "Enter the issue date.",
      /*
       * `dueRuleShort`, not `dueRule`: this hint used to interpolate the full rule SENTENCE, so it
       * read "Leave blank to use Payment is due at the end of the month the invoice falls in, plus
       * 7 days.." — two verbs and two full stops. A sentence does not go inside a sentence.
       */
      /*
       * D75 — "this account's rule", not "the portal's rule". The rule interpolated here is now
       * resolved per retailer, so naming the portal would be wrong for exactly the accounts that
       * have a term of their own.
       */
      "dueOn": "Due date", "dueHint": "Filled in from this account's rule: {{rule}}. Change it to match the invoice.",
      /* D104 — the two ends of the window, shown as two dates when the rule has a range (month end → month end + grace). */
      "minDueOn": "Earliest due date (month end)", "maxDueOn": "Latest due date",
      /* Per-piece charges on Record invoice. */
      "ringSize": "Size {{size}}",
      "courier": "Courier", "misc": "Miscellaneous",
      "courierFor": "Courier charge for {{job}}", "miscFor": "Miscellaneous charge for {{job}}",
      "subtotal": "Pieces", "invoiceTotal": "Invoice total",
      "chargeInvalid": "Enter charges as plain amounts, such as 25 or 12.50. Leave blank for none.",
      /* D104 — what the rule gives for the issue date. MONTH_END has a range; NET_DAYS has one date and no grace. */
      "windowRange": "Falls due {{due}}; the last date before it is late is {{late}}.",
      "windowOne": "Falls due {{due}}.", "dueBeforeIssue": "The due date cannot be before the issue date.",
      "record": "Record invoice",
      /*
       * Both name what the caller was already handing them and the sentence was dropping.
       *
       * The title says whose account is about to be credited — `invoiceTitle` above does, and a
       * money dialog that names nobody is the one you confirm against the wrong retailer. The
       * amount field states its currency because a payment is received in exactly one (AN-08),
       * and an amount box beside a table of several currencies has to say which it means.
       */
      "paymentTitle": "Confirm payment — {{retailer}}",
      "amount": "Amount received ({{currency}})",
      "receivedOn": "Date received", "receivedFuture": "The date received cannot be in the future.",
      "bankReference": "Bank reference",
      /*
       * `{{reference}}` — the aria-label on ONE row's allocation box, so it has to name that row.
       * Without it every input in the column answered to the same accessible name, which is no
       * name at all to anyone navigating by them. The caller has always passed the reference.
       */
      "allocateTo": "Apply to invoice {{reference}}", "allocate": "Apply",
      /*
       * `<m/>`, not `{{amount}}`. A figure of money is rendered by `<Money>` — invariant 1 keeps
       * it a decimal STRING with a currency beside it, so there is no formatted string a caller
       * could interpolate, and both call sites were putting the component next to the sentence
       * and passing nothing. Two `{{amount}}` pairs reached the screen on a line about money:
       * "Applied: {{amount}} USD 3,180 · {{amount}} will be left as a credit. USD 0".
       *
       * A component SLOT is the mechanism the catalog already uses for this (`<m/>` in
       * `shop.order.overBudget`, `retailerBudget.tolerance.ceiling`), rendered by `<Trans>`. It
       * also keeps the word order the sentence's own: "{amount} will be left as a credit" leads
       * with the figure, and a translation — D47's Arabic among them — may need it elsewhere.
       */
      "allocated": "Applied: <m/>",
      /** `allocationCheck`'s refusals, keyed by its `problem` union. */
      "problem": {
        "AMOUNT": "Enter the amount received, as a number such as 840.00.",
        "LINE": "The amount against invoice {{reference}} is not a number.",
        "OVER_INVOICE": "That is more than invoice {{reference}} still owes.",
        "OVER_PAYMENT": "The amounts applied come to more than the payment.",
        "NOTHING_ALLOCATED": "Apply the payment to at least one invoice."
      },
      "creditLeft": "<m/> will be left as a credit.",
      "confirm": "Confirm payment",
      "reverseTitle": "Reverse the sale of {{job}}",
      /* "this retailer" when the caller is holding the retailer's name — say it. */
      "reverseBody": "The piece goes back to being held, and its value leaves {{retailer}}'s payable. The reversal stays on the record.",
      "reason": "Reason", "reasonHint": "Why this sale is being reversed.",
      "reverse": "Reverse sale",
      "cancel": "Cancel"
    }
  },
  /*
   * R-27 — one purchase invoice, as the retailer reads it (CR-56 P1). The pieces need not add up to the
   * invoice total, because tax is posted separately (FN-17), so the page says "adjustments" rather
   * than implying an error.
   */
  "purchaseInvoice": {
    "back": "Payables",
    "sub": "Issued {{issued}} · Due {{due}}",
    "loadFailed": "This invoice could not be loaded.",
    "notFound": "We could not find that invoice.",
    "piecesTitle": "Pieces on this invoice", "piecesEmpty": "No pieces are recorded against this invoice.",
    "colJob": "Job number", "colDescription": "Description", "colValue": "Wholesale value",
    "subtotal": "Pieces subtotal", "adjustments": "Adjustments (tax and other)", "total": "Invoice total",
    "paymentsTitle": "Payments applied", "paymentsEmpty": "Nothing has been paid against this invoice yet.",
    "colReceived": "Received", "colBankReference": "Bank reference", "colAmount": "Applied",
    "paid": "Paid", "outstanding": "Outstanding",
    "download": "Download PDF", "downloadFailed": "The PDF could not be downloaded.",
    /* CR-56 P4 — the retailer's claims. A claim is not a payment, and the copy never lets it read as one. */
    "submissionsTitle": "Payments you have sent",
    "submissionsNote": "These are waiting for Miraki to confirm them. Until then they are not counted as paid, and the amount outstanding above does not change.",
    "colSubmitted": "Sent", "colStatus": "Status", "colProof": "Proof", "viewProof": "View", "noProof": "Not attached",
    "submissionStatus": { "PENDING": "Awaiting confirmation", "REJECTED": "Not accepted" },
    "pay": {
      "title": "Pay this invoice",
      "intro": "Pay by bank transfer, quoting the invoice reference, then tell us here. Miraki confirms each payment once it arrives — until then this invoice still shows what is outstanding.",
      "amount": "Amount paid", "paidOn": "Date paid", "bankReference": "Bank reference", "bankReferenceHint": "The reference on your transfer.",
      "proof": "Proof of payment", "proofHint": "Optional. A photo or PDF of the transfer (JPEG, PNG, WebP or PDF, up to 15MB).",
      "send": "Send for confirmation", "sending": "Sending…",
      "sent": "Sent. Miraki will confirm it once the payment is received.",
      "sentNoFile": "Sent, but the proof could not be attached. Miraki can still confirm the payment; you can send the file again from this page.",
      "failed": "That payment could not be sent. Check the amount and try again.",
      "amountInvalid": "Enter the amount you paid.",
      "amountTooHigh": "That is more than is still outstanding on this invoice."
    }
  },
  "payables": {
    "title": "Payables",
    "asOf": "{{portal}} · as of {{asOf}}",
    "notYet": "Your payables appear here once consignment is live.",
    "outstanding": "Outstanding", "outstandingHint": "Pieces you have sold and not yet paid for.",
    "overdue": "Overdue", "overdueChip": "Overdue",
    "nextDue": "Next due", "noneDue": "Nothing due",
    "nothingOwed": "You owe nothing at the moment.",
    "nothingOverdue": "Nothing is overdue.",
    "creditOf": "In credit:",
    /*
     * FN-05 and FN-11's due rule as words — the rule IN FORCE (D75), which is this account's own
     * payment term where it has one and the portal's setting where it does not.
     *
     * The KEY is chosen by `dueRuleWords()` from the resolved rule, so both SHAPES exist here and
     * neither is a default. An account on a MONTH_END portal must not read "30 days from the
     * invoice date": that is a different rule, and before D75 four screens said exactly that off
     * a `payment_terms_days` column nothing honoured.
     */
    "dueRule": {
      "netDays": "Payment is due {{days}} days from the invoice date.",
      "monthEnd": "Payment is due at the end of the month the invoice falls in, plus {{days}} days."
    },
    /** The same rule as a PHRASE, for the places that name it inside a sentence of their own. */
    "dueRuleShort": {
      "netDays": "{{days}} days from the invoice date",
      "monthEnd": "month end plus {{days}} days"
    },
    /*
     * The shortest reading, for a table cell under a column header that already says "Terms".
     *
     * "Net 30" survives here and ONLY here, because this is the one place the surrounding column
     * supplies the missing half of the sentence. The month-end shape still needs its own words —
     * there is no net-days number that can stand for it, which is the whole point.
     */
    "dueRuleTerm": {
      "netDays": "Net {{days}}",
      "monthEnd": "Month end + {{days}}"
    },
    /*
     * D75 — WHICH rung of the ladder answered, in the two voices that ask.
     *
     * `admin` separates a commercial agreement with this customer from a default that moves when
     * the portal setting does. `retailer` answers a different question — is this mine to discuss
     * — which is why the same rung reads differently on R-22 than on A-04.
     */
    "dueRuleSource": {
      "admin": {
        "retailer": "This account's own payment term.",
        "portal": "This portal's rule — this account has no term of its own.",
        "default": "The platform default — neither this account nor this portal has set a rule."
      },
      "retailer": {
        "retailer": "This is the payment term agreed for your account.",
        "portal": "This is set by Miraki for your portal.",
        "default": "This is set by Miraki."
      }
    },
    /* D75 — `dueFixed` ("This is set by Miraki for your portal.") is replaced by
       `dueRuleSource.retailer.*`, which reads the rule's resolved source. It was unconditional,
       and it is false for an account with a payment term of its own — it disclaimed a term the
       retailer had actually agreed to. */
    "howToPay": "Pay by bank transfer using the reference on the invoice. Miraki confirms each payment once it arrives.",
    "proofComing": "Uploading a proof of payment is coming.",
    "tabInvoices": "Invoices", "tabPayments": "Payments", "tabStatement": "Statement",
    "colReference": "Reference", "colIssued": "Issued", "colDue": "Due", "colAmount": "Amount", "colOpen": "Open", "colState": "State",
    /** The same four invoice states as the desk's. */
    "state": {
      "ISSUED": "Issued",
      "PART_PAID": "Part paid",
      "PAID": "Paid",
      "CANCELLED": "Cancelled"
    },
    /*
     * FN-14 — the statement's entry types, keyed by `mki_ledger_entries.entry_type`. Every value
     * of that enum has a key here, including the pre-v1.6 ones: the ledger is append-only
     * (invariant 2), so a retired entry type still has rows on a statement for ever.
     */
    "entry": {
      "SALE": "Piece sold",
      "SALE_REVERSAL": "Sale reversed",
      "LOSS_CHARGE": "Piece lost, damaged or stolen",
      "INVOICE": "Invoice",
      "PAYMENT_RECEIVED": "Payment received",
      "ADVANCE_PAYMENT": "Advance payment",
      "MID_PAYMENT": "Stage payment",
      "FINAL_PAYMENT": "Final payment",
      "SHIPPING_CHARGE": "Shipping charge",
      "MISC_CHARGE": "Other charge",
      "CREDIT_ADJUSTMENT": "Credit adjustment",
      "DEBIT_ADJUSTMENT": "Debit adjustment"
    },
    "invoicesEmpty": "No invoices yet", "invoicesEmptyBody": "An invoice appears here when Miraki raises one for pieces you have sold.",
    "colReceived": "Received", "colBankReference": "Bank reference", "colAppliedTo": "Applied to",
    /*
     * The retailer's half of the same defect as `payablesDesk.form.allocated`, found by the rule
     * rather than by a reader: this declared `{{amount}}`, its call site passed nothing and put a
     * `<Money>` beside it, so R-22's Applied-to column read "{{amount}} left as a credit USD 90".
     *
     * A LABEL, not an interpolation — because that is what this namespace does everywhere else:
     * `creditOf` above is "In credit:" followed by `<Money>`, called in exactly the same shape by
     * the same screen. The string was the odd one out, so the string is what changed; no slot is
     * needed where the figure already comes last.
     */
    "leftAsCredit": "Left as a credit:",
    "paymentsEmpty": "No payments yet", "paymentsEmptyBody": "Payments Miraki has confirmed appear here.",
    "statementIn": "Statement in {{currency}}",
    "statementNote": "Every entry on your account, oldest first, with the balance after each.",
    "colDate": "Date", "colEntry": "Entry", "colDebit": "Owed", "colCredit": "Paid or credited", "colBalance": "Balance",
    "closing": "Closing balance",
    "statementEmpty": "Nothing on your statement yet", "statementEmptyBody": "Entries appear here as you sell pieces and settle invoices."
  },
  "order": {
    "milestones": {
      "title": "Payment milestones",
      "agreedTotal": "Agreed order total",
      "currentMilestone": "Current milestone",
      "amountDueNow": "Amount due now",
      "verified": "Verified",
      "pending": "Awaiting verification",
      "remaining": "Remaining balance",
      "allPaid": "All milestones are paid",
      "colMilestone": "Milestone",
      "colShare": "Share",
      "colAmount": "Amount",
      "colCumulative": "Paid by this point",
      "colStatus": "Status",
      "gate_beginManufacture": "M{{n}} — before manufacturing begins",
      "gate_markReady": "M{{n}} — before the order is marked ready",
      "gate_ship": "M{{n}} — before shipment",
      "gate_markDelivered": "M{{n}} — before delivery completes",
      "statusPaid": "Paid",
      "statusDue": "Due now",
      "statusUpcoming": "Upcoming",
      "proofPending": "Awaiting verification",
      "proofVerified": "Verified",
      "proofRejected": "Rejected",
      "rejectionReason": "Reason: {{reason}}",
      "resubmit": "Submit the payment again",
      "futureLocked": "This milestone is not due yet.",
      "forInvoice": "For consignment invoice",
      "orderLevel": "Not for a specific consignment invoice (shared across all of them)",
      "attribute": "Assign to invoice",
      "attributeFailed": "Could not assign this payment to that invoice.",
      "overCap": "This payment would exceed the agreed order total.",
      "refundTitle": "Refund",
      "refundAction": "Record refund",
      "refundReason": "Reason for the refund",
      "refundConfirm": "Refund the full verified amount of {{amount}}",
      "refundDone": "Refund recorded.",
      "refundNothing": "There is nothing left to refund on this order.",
      "legacyNotice": "This order follows the payment terms it was approved under.",
      "amountOver": "That is more than can be paid now. The most you can pay at this point is {{amount}}.",
      "amountUnder": "That is less than the {{due}} due now. The milestone stays open until the remaining {{owed}} is paid.",
      "amountInvalid": "Enter an amount greater than zero, for example 1250.00.",
      "dueNow": "Due now: {{amount}}",
      "payableNow": "You can pay up to {{amount}} now.",
      "nothingPayable": "Nothing can be paid right now.",
      "heldUntilReady": "The next payment can be made once the order has been marked ready.",
      "submitted": "Payment submitted. Miraki will verify it before the next step goes ahead."
    }
  },
  "declarations": {
    "tile": "Sales not declared",
    "tileDue": "Due by {{deadline}}",
    "tileLate": "Was due {{deadline}}",
    "tileHint_one": "{{count}} account holding pieces has reported no sale for this period",
    "tileHint_other": "{{count}} accounts holding pieces have reported no sale for this period",
    "tab": "Declarations",
    "title": "Sales not declared",
    "period": "{{from}} to {{to}}",
    "deadline": "This month's sales must all be declared by {{deadline}}. That is not the payment date: the payable falls due separately.",
    "backstop": "A sale can be dated back no further than {{earliest}}, so a retailer reporting today cannot give a true date for a sale made before then. Sales are meant to be reported as they happen, not once a month.",
    "conversation": "This is a conversation list, not a compliance check. It shows accounts holding pieces that have reported no sale for this period — which may mean a quiet month, or a month nobody told us about. Only the account can say which.",
    "colRetailer": "Retailer",
    "colPieces": "Pieces held",
    "colLastReported": "Last sale reported",
    "never": "Never",
    "empty": "Every account holding pieces has reported a sale",
    "emptyBody": "Nothing to chase for this period. It means each of them has told us something, not that each has told us everything.",
    "state": {
      "OPEN": "Open",
      "DUE": "Due",
      "LATE": "Late"
    },
    "myDue": "Report every sale you have made this month by {{deadline}}.",
    "myLate": "This month's sales were due by {{deadline}} and we have none from you yet. Report them now — a late report is still recorded, and reporting a sale is never refused.",
    "myBackstop": "A sale can be dated back no further than {{earliest}}, so report sales as they happen rather than once a month.",
    "myDeclared": "You have reported sales for this period. The month closes {{deadline}}; report anything further as it happens."
  }
} as const;
